import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Phone, Search, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Loader2, AlertTriangle, CheckCircle, XCircle, X,
  User, Clock, Mic, MessageSquare, Shield,
  Star, BookOpen, TrendingDown, TrendingUp, Sparkles,
  ChevronDown, ChevronUp, Info, Maximize2, Save, Edit2, SlidersHorizontal,
  BarChart2, Database,
} from 'lucide-react';
import api from '../services/api';

const formatDuration = (sec) => {
  if (sec == null) return '—';
  const n = Number(sec);
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const channelLabel = (c) => (!c || c === 'null') ? 'Voice' : c === 'EmbeddedMessaging' ? 'Chat' : c;

const computeRuleBasedEvaluation = (call, transcriptText) => {
  const rawLines = transcriptText ? transcriptText.split('\n').filter(l => l.trim()) : [];
  const agentLines = [], memberLines = [], allParsed = [];

  const firstName = (call.agent_name || '').split(' ')[0].toLowerCase();

  for (const line of rawLines) {
    const withoutTs = line.replace(/^\[\d+:\d+\s*\|\s*[\d:]+\s*[AP]M\]\s*/i, '').trim();
    const colonIdx = withoutTs.indexOf(':');
    if (colonIdx === -1) continue;
    const speaker = withoutTs.substring(0, colonIdx).trim();
    const text = withoutTs.substring(colonIdx + 1).trim();
    if (!text) continue;
    const isAgent = speaker.toLowerCase() === 'agent' || (firstName.length > 2 && speaker.toLowerCase().includes(firstName));
    const entry = { speaker, text, isAgent, raw: line };
    allParsed.push(entry);
    if (isAgent) agentLines.push(entry);
    else memberLines.push(entry);
  }

  const agentTextLower = agentLines.map(l => l.text).join(' ').toLowerCase();
  const memberTextLower = memberLines.map(l => l.text).join(' ').toLowerCase();
  const hasTranscript = allParsed.length >= 3;

  // Program detection from transcript content (includes common TTS mishearing variants)
  const fullText = agentTextLower + ' ' + memberTextLower;
  const detectedProgram = (() => {
    if (fullText.includes('kaiser permanente') || fullText.includes('kaiser permanency') || fullText.includes('kaiser permanence') || fullText.includes('healthy living wallet') || fullText.includes('my health adventure')) return 'Kaiser Permanente';
    if (fullText.includes('navitus')) return 'Navitus';
    // WI-04: HAP phonetic TTS variants — "hat", "half", "hot" as standalone words are confirmed misrenderings of "HAP"
    if (/\bhap\b/.test(fullText) || fullText.includes('hap flex') || fullText.includes('hat flex') || fullText.includes('half flex') || fullText.includes('hot flex') || fullText.includes('healthy living rewards') || fullText.includes('benefits purse') || fullText.includes('rewards purse') || fullText.includes('medline') || /\b(hat|half|hot)\s+(benefits|rewards|card|program|flex)\b/.test(fullText)) return 'HAP';
    if (fullText.includes('sunny direct') || (fullText.includes('sunny') && !fullText.includes('kaiser') && !fullText.includes('navitus') && !fullText.includes('hap'))) return 'Sunny Direct';
    return 'Unknown';
  })();

  // Strict agent lines: only where speaker label is exactly "agent" or exactly the agent's name
  // Used for closing/further-questions checks to avoid attributing caller speech to the agent
  const agentFullName = (call.agent_name || '').toLowerCase();
  const strictAgentLines = allParsed.filter(l => {
    const sp = l.speaker.toLowerCase();
    return sp === 'agent' || (agentFullName && sp === agentFullName) || (firstName.length > 2 && sp === firstName);
  });
  const strictAgentText = strictAgentLines.length > 0
    ? strictAgentLines.map(l => l.text).join(' ').toLowerCase()
    : agentTextLower;
  const items = [];

  // Item 1: Allowed caller to explain without interrupting (5 pts)
  {
    let score = 0, notes;
    if (!hasTranscript) {
      notes = 'Unable to Confirm — transcript unavailable or too short to assess';
    } else {
      // Strongest evidence: caller explicitly says they were cut off (WI-08)
      const callerInterruptionPhrases = ["can i finish", "hold on let me finish", "i wasn't done talking", "you cut me off", "let me finish", "i'm not done", "i was still talking"];
      const callerExplicitlyInterrupted = callerInterruptionPhrases.some(p => memberTextLower.includes(p));
      // Positive indicator: agent self-corrects overlap with "go ahead" / "please continue" (WI-08)
      const agentSelfCorrected = ['go ahead', 'please continue', 'sorry, go ahead', "didn't mean to cut you off", 'i apologize, please go ahead'].some(p => agentTextLower.includes(p));
      const shortTurns = memberLines.filter(l => l.text.split(/\s+/).length < 4).length;
      const shortRatio = memberLines.length > 0 ? shortTurns / memberLines.length : 0;
      if (callerExplicitlyInterrupted) {
        score = 0;
        const triggeredPhrase = callerInterruptionPhrases.find(p => memberTextLower.includes(p));
        notes = `Interruption confirmed — caller explicitly stated they were cut off: "${triggeredPhrase}" (WI-08)`;
      } else if (agentSelfCorrected) {
        score = 5;
        notes = 'Agent used self-correcting language ("go ahead" / "please continue") — positive active-listening indicator per WI-08; full credit awarded';
      } else if (shortRatio > 0.7 && memberLines.length > 3) {
        score = 3;
        notes = `${shortTurns} of ${memberLines.length} member turns were under 4 words — possible interruption pattern; partial credit awarded`;
      } else {
        score = 5;
        notes = `Member had ${memberLines.length} speaking turns in available transcript — no interruption pattern detected`;
      }
    }
    items.push({ section: 'General Experience', criterion: 'Allowed caller to fully explain concerns without interrupting', awardedPoints: score, maxPoints: 5, notes });
  }

  // Item 2: Acknowledged concern + personalization (5 pts)
  {
    const ackPhrases = ['i understand', 'i can help', 'i see', 'absolutely', 'of course', 'certainly', 'i appreciate', 'happy to help', 'glad to help'];
    const foundAck = ackPhrases.find(p => agentTextLower.includes(p));
    const memberFirstWords = (memberLines[0]?.text.split(/\s+/) || [])
      .map(w => w.toLowerCase().replace(/[.,!?'"]/g, ''))
      .filter(w => w.length >= 3);
    const usedName = memberFirstWords.some(w => agentTextLower.includes(w));
    let score = 0, notes;
    if (!hasTranscript) {
      notes = 'Unable to Confirm — insufficient transcript to assess';
    } else if (foundAck && usedName) {
      score = 5;
      notes = `Acknowledgment detected ("${foundAck}") and caller name used in conversation`;
    } else if (foundAck || usedName) {
      score = 3;
      notes = foundAck ? `Acknowledgment phrase found ("${foundAck}") — caller name usage not confirmed` : 'Caller name used — explicit acknowledgment phrase not detected';
    } else {
      notes = 'No acknowledgment phrases or caller name usage detected in available transcript';
    }
    // Program terminology check — check each violation independently (multiple can co-occur)
    if (hasTranscript && detectedProgram !== 'Unknown') {
      const termViolations = [];
      if (detectedProgram === 'Kaiser Permanente') {
        if (agentTextLower.includes('purse') && !agentTextLower.includes('wallet')) termViolations.push('Used "Purse" on Kaiser Permanente call — should be "Wallet"');
        if (/\bsunny\b/.test(agentTextLower)) termViolations.push('Mentioned "Sunny" on Kaiser Permanente call — never permitted under any circumstance (WI-18)');
        // KP abbreviation rule: must always say "Kaiser Permanente" — never "KP" or "Kaiser" alone
        if (/\bkp\b/.test(agentTextLower) || (agentTextLower.includes('kaiser') && !agentTextLower.includes('kaiser permanente') && !agentTextLower.includes('kaiser permanency') && !agentTextLower.includes('kaiser permanence'))) termViolations.push('Used "KP" or "Kaiser" alone — must always say "Kaiser Permanente" when addressing the member');
      } else if (detectedProgram === 'HAP') {
        if (agentTextLower.includes(' wallet') && !agentTextLower.includes('purse')) termViolations.push('Used "Wallet" on HAP call — should be "Purse" or "Benefits Purse"');
        if (agentTextLower.includes('nations benefits')) termViolations.push('Referred to the HAP card as "Nations Benefits" — incorrect; this is the old card brand, not the current 2026 HAP card');
        // WI-18: Sunny unprompted on HAP call is a white-label violation (scored in Item 2 per Excel)
        if (/\bsunny\b/.test(agentTextLower) && !/\bsunny\b/.test(memberTextLower)) termViolations.push('Mentioned "Sunny" unprompted on HAP call — white-label violation (WI-18); only permitted if member asks first');
      } else if (detectedProgram === 'Navitus') {
        if (agentTextLower.includes(' member ') && !agentTextLower.includes('associate')) termViolations.push('Used "Member" on Navitus call — caller should be referred to as "Associate"');
        // WI-18: Sunny outside app context on Navitus call is a white-label violation (scored in Item 2 per Excel)
        if (/\bsunny\b/.test(agentTextLower) && !agentTextLower.includes('sunny application') && !agentTextLower.includes('sunny app') && !agentTextLower.includes('sunny payments')) termViolations.push('Referenced "Sunny" outside app context on Navitus call — only permitted when discussing the Sunny Payments & Benefits Application (WI-18)');
      }
      if (termViolations.length > 0) {
        score = Math.max(0, score - 2 * termViolations.length);
        const tvNote = `Terminology violation(s): ${termViolations.join('; ')}`;
        notes = notes ? `${notes}; ${tvNote}` : tvNote;
      }
    }
    items.push({ section: 'General Experience', criterion: 'Acknowledged concern and used appropriate personalization', awardedPoints: score, maxPoints: 5, notes });
  }

  // Item 3: Mirrored tone (5 pts)
  // Text-based proxy: caller frustration → agent empathy, curt language, positive tone mirroring
  // Audio review is required to confirm actual vocal tone; these are transcript-level indicators only
  {
    let score = 0;
    let notes;
    if (!hasTranscript) {
      notes = 'Unable to Confirm — transcript unavailable';
    } else {
      const callerFrustrationPhrases = ["this is ridiculous", "i've called", "i have called", "i'm so frustrated", "i am so frustrated", "this is unacceptable", "nobody has helped me", "nobody helped me", "i'm upset", "i cannot believe", "i can't believe this", "this is terrible", "very frustrated"];
      const callerFrustrated = callerFrustrationPhrases.some(p => memberTextLower.includes(p));
      const agentEmpathyPhrases = ["i completely understand your frustration", "i understand your frustration", "i'm sorry you're dealing with this", "i can imagine how frustrating", "i'm so sorry", "i apologize for the inconvenience", "i understand how you feel", "i'm sorry to hear", "i understand that must be frustrating", "that must be really frustrating"];
      const agentEmpathized = agentEmpathyPhrases.some(p => agentTextLower.includes(p));
      // Only phrases that are unambiguously curt — NOT dual-use closing phrases like "anything else?" which Item 8 treats as correct behavior
      const agentCurtPhrases = ["okay. next", "moving on.", "next issue", "next question"];
      const agentCurt = agentCurtPhrases.some(p => agentTextLower.includes(p)) && !agentEmpathized;
      const callerPositivePhrases = ["oh that's great", "i'm so excited", "that's exciting", "oh wonderful", "that is great"];
      const callerPositive = callerPositivePhrases.some(p => memberTextLower.includes(p));
      const agentWarmthPhrases = ["congratulations", "that's wonderful", "that's great to hear", "i love that for you", "how wonderful", "i'm happy to hear"];
      const agentWarm = agentWarmthPhrases.some(p => agentTextLower.includes(p));

      if (agentCurt) {
        score = 0;
        const found = agentCurtPhrases.find(p => agentTextLower.includes(p));
        notes = `Curt or transactional language detected ("${found}") — tone does not appear to mirror caller; audio review recommended to confirm`;
      } else if (callerFrustrated && !agentEmpathized) {
        // Rubric: one noticeable mismatch = partial credit; repeated/worsening = zero
        const frustPhrase = callerFrustrationPhrases.find(p => memberTextLower.includes(p));
        const frustCount = callerFrustrationPhrases.filter(p => memberTextLower.includes(p)).length;
        if (frustCount >= 2) {
          score = 0;
          notes = `Multiple frustration signals from caller (${frustCount} detected, e.g. "${frustPhrase}") with no empathy response — repeated tone mismatch; audio review recommended`;
        } else {
          score = 2;
          notes = `Single frustration signal ("${frustPhrase}") with no detected empathy response — partial credit per rubric (one missed empathy cue); audio review recommended`;
        }
      } else if (callerFrustrated && agentEmpathized) {
        score = 5;
        const empPhrase = agentEmpathyPhrases.find(p => agentTextLower.includes(p));
        notes = `Caller frustration detected and agent empathy response found ("${empPhrase}") — tone mirroring indicated; audio confirmation recommended for full credit`;
      } else if (callerPositive && agentWarm) {
        score = 5;
        const warmPhrase = agentWarmthPhrases.find(p => agentTextLower.includes(p));
        notes = `Positive caller tone with agent warmth response ("${warmPhrase}") — tone mirroring indicated; audio confirmation recommended`;
      } else {
        // Neutral call: no frustration, no warmth signals, no curt language — no evidence of mismatch
        // Cannot confirm tone from text alone; award provisional 2/5 pending audio review
        score = 2;
        notes = 'No tone mismatch detected in transcript (no frustration, curt language, or warmth signals) — provisional 2/5 pending audio review; reviewer should adjust based on actual vocal tone';
      }
    }
    items.push({ section: 'General Experience', criterion: 'Mirrored tone appropriately', awardedPoints: score, maxPoints: 5, notes });
  }

  // Item 4: Managed pauses and hold times (10 pts — Pass/Fail)
  {
    let score = 10;
    const notes = [];
    const holdPermPhrases = ['may i place you on hold', 'can i put you on hold', 'do you mind if i place', 'may i put you on hold', 'is it okay if i put you', 'place you on a brief hold'];
    const askedPermission = holdPermPhrases.some(p => agentTextLower.includes(p));
    const holdTimeSec = Number(call.hold_time) || 0;

    const holdReasonPhrases = ['hold to', 'hold while i', 'hold so i can', 'hold so that i', 'hold because', 'hold to look', 'hold to check', 'placing you on hold to', 'hold for a moment while', 'hold as i', 'while i pull up', 'while i look into', 'while i check'];
    const explainedReason = holdReasonPhrases.some(p => agentTextLower.includes(p));

    if (holdTimeSec > 0 && !askedPermission) {
      score = 0;
      notes.push(`Hold time ${formatDuration(holdTimeSec)} recorded — no hold permission phrase detected (rubric requires permission before hold)`);
    } else if (holdTimeSec > 0 && askedPermission && !explainedReason) {
      score = 0;
      notes.push(`Hold permission asked but no explanation for the hold detected — rubric requires agent to explain why hold is needed`);
    } else if (holdTimeSec > 0 && askedPermission && explainedReason) {
      notes.push(`Hold permission asked and reason explained; hold time ${formatDuration(holdTimeSec)}`);
    } else {
      notes.push('No hold time recorded — hold management criteria not applicable');
    }

    // 90-second hold check-in (WI-02: agent must check in if hold exceeds 90s)
    if (holdTimeSec > 90) {
      const checkInPhrases = ["thanks for your patience", "thank you for your patience", "still working on this", "just a little longer", "almost there", "still looking into"];
      const hasCheckIn = checkInPhrases.some(p => agentTextLower.includes(p));
      if (!hasCheckIn) {
        score = 0;
        notes.push(`Hold exceeded 90 seconds (${formatDuration(holdTimeSec)}) with no check-in phrase detected — required per WI-02`);
      } else {
        notes.push('Hold check-in phrase detected — meets WI-02 requirement for extended holds');
      }
    }

    // Dead air check from transcript timestamps (verbal bridges reset the 9s clock per WI-02)
    const deadAirBridgePhrases = ['one moment', 'bear with me', 'let me take a look', 'let me pull', 'let me check', 'just a moment', 'give me a moment', 'thank you for holding'];
    let maxUnbridgedPauseSec = 0;
    for (let i = 1; i < rawLines.length; i++) {
      const prev = rawLines[i - 1].match(/^\[(\d+):(\d+)/);
      const curr = rawLines[i].match(/^\[(\d+):(\d+)/);
      if (prev && curr) {
        const gap = (parseInt(curr[1]) * 60 + parseInt(curr[2])) - (parseInt(prev[1]) * 60 + parseInt(prev[2]));
        // Check if the preceding line contained a verbal bridge (resets the clock)
        const prevLineText = rawLines[i - 1].toLowerCase();
        const hasBridge = deadAirBridgePhrases.some(p => prevLineText.includes(p));
        if (!hasBridge && gap > maxUnbridgedPauseSec) maxUnbridgedPauseSec = gap;
      }
    }
    if (maxUnbridgedPauseSec > 9) {
      score = 0;
      notes.push(`Dead air detected: longest unbridged pause was ${maxUnbridgedPauseSec}s (rubric allows max 9s; verbal bridges reset the clock per WI-02)`);
    } else if (maxUnbridgedPauseSec > 0) {
      notes.push(`Longest transcript gap: ${maxUnbridgedPauseSec}s — within acceptable range`);
    }

    items.push({ section: 'General Experience', criterion: 'Managed long pauses and hold times effectively', awardedPoints: score, maxPoints: 10, notes: notes.join('; ') });
  }

  // Item 5: Demonstrated confidence / filler words (10 pts)
  {
    let fillerCount = 0;
    const singleFillers = new Set(['um', 'uh']);
    for (const al of agentLines) {
      const t = al.text.toLowerCase();
      t.split(/\s+/).forEach(w => {
        if (singleFillers.has(w.replace(/[.,!?'"]/g, ''))) fillerCount++;
      });
      // "like" as filler (WI-05) — count only when used as a discourse particle, not descriptively
      // Conservative pattern: comma-bounded, or following known filler openers
      fillerCount += (t.match(/,\s*like\s*,|\bso\s+like\b|\bum\s+like\b|\buh\s+like\b|\bi\s+mean\s+like\b|\band\s+like\b/g) || []).length;
    }
    let score, notes;
    if (!hasTranscript) {
      score = 0; notes = 'Unable to Confirm — no transcript available';
    } else if (fillerCount <= 3) {
      score = 10; notes = `${fillerCount} filler word(s) detected (um, uh, like) — excellent (rubric: 0–3 = full points)`;
    } else if (fillerCount <= 8) {
      score = 5; notes = `${fillerCount} filler words detected — moderate (rubric: 4–8 = partial credit)`;
    } else {
      score = 0; notes = `${fillerCount} filler words detected — exceeds threshold (rubric: >8 = no points)`;
    }
    // Rubric: vague/hedging language reduces caller trust and signals lack of preparedness
    const hedgingPhrases = ["i think i can help", "i'll try to", "i'll try and", "i guess that should", "maybe i can", "i'm not sure if i can", "i hope that works", "i think that's right"];
    const foundHedging = hedgingPhrases.find(p => agentTextLower.includes(p));
    if (foundHedging && hasTranscript) {
      score = Math.max(0, score - 3);
      notes = `${notes}; Hedging language detected: "${foundHedging}" — rubric contrasts this against confident, specific language`;
    }
    items.push({ section: 'General Experience', criterion: 'Demonstrated confidence in delivery', awardedPoints: score, maxPoints: 10, notes });
  }

  // Item 6: Accurate and complete information (25 pts)
  {
    const evidence = [], deductions = [];
    let score = 25;
    if (!hasTranscript) {
      score = 0;
      evidence.push('Unable to Evaluate from Provided Materials — no transcript available');
    } else {
      const intents = [call.primary_intent_a, call.primary_intent_b, call.primary_intent_c].filter(Boolean).map(i => i.toLowerCase());

      // KB: 313-664-8896 is the HAP Customer Service line — correct to give ONLY in 3-way authorized user context; penalize if given as a general member number outside that context
      if (agentTextLower.includes('313-664-8896') && !agentTextLower.includes('three-way') && !agentTextLower.includes('3-way') && !agentTextLower.includes('authorized user') && !agentTextLower.includes('hap customer service')) {
        score = Math.max(0, score - 10);
        deductions.push('KB violation: 313-664-8896 (HAP Customer Service) given outside of 3-way authorized user context — this number is only for the 3-way call process, not a general member-facing number');
      }
      // Investigative language — Full Credit Indicator (all programs, Excel Item 6)
      const investigativePhrases = ['let me investigate this further', "i'm going to double-check", "i'm going to double check", 'let me double-check', 'let me double check', 'let me verify this', 'want to verify before', 'double-check just to ensure', 'make sure i have the right answer', 'let me confirm that for you', 'i want to make sure i give you the correct'];
      const foundInvestigative = investigativePhrases.find(p => agentTextLower.includes(p));
      if (foundInvestigative) {
        score = Math.min(25, score + 5);
        evidence.push(`Investigative language detected — agent verified before answering: "${foundInvestigative}" (Full Credit Indicator)`);
      }
      // KB: Correct HAP member-facing number
      if (agentTextLower.includes('1-800-801-1770') || agentTextLower.includes('800-801-1770')) {
        score = Math.min(25, score + 5);
        evidence.push('Correct HAP member number provided (1-800-801-1770 per KB)');
      }
      // KB: Card-related checks — HAP-specific timeline (10–13 business days)
      if (intents.some(i => i.includes('card'))) {
        if (detectedProgram === 'HAP' && agentTextLower.includes('business day') && (agentTextLower.includes('10') || agentTextLower.includes('13'))) {
          score = Math.min(25, score + 3);
          evidence.push('HAP card delivery timeframe mentioned — aligns with KB (10–13 business days)');
        }
        // Kaiser-specific card timeline: 7–10 business days
        if (detectedProgram === 'Kaiser Permanente' && agentTextLower.includes('business day') && (agentTextLower.includes('7') || agentTextLower.includes('seven'))) {
          score = Math.min(25, score + 3);
          evidence.push('Kaiser card delivery timeframe mentioned — aligns with KB (7–10 business days)');
        }
        if (agentTextLower.includes('last 4') || agentTextLower.includes('last four')) {
          score = Math.min(25, score + 2);
          evidence.push('Last 4 digits verification mentioned — aligns with KB card activation process');
        }
      }
      // KB: Dispute/fraud checks — HAP investigative questions (WI-19) + general dispute process
      // Detect dispute/decline/fraud from either intent fields OR transcript keywords (Gap 15)
      const transcriptDeclineSignals = agentTextLower.includes('decline') || agentTextLower.includes('declined') || memberTextLower.includes('decline') || memberTextLower.includes('declined') || memberTextLower.includes('didn\'t work') || memberTextLower.includes('card didn\'t') || memberTextLower.includes('transaction') || memberTextLower.includes('fraud') || memberTextLower.includes('dispute');
      if (intents.some(i => i.includes('dispute') || i.includes('fraud') || i.includes('decline')) || transcriptDeclineSignals) {
        if (agentTextLower.includes('freeze') || agentTextLower.includes('tier 2') || agentTextLower.includes('case')) {
          score = Math.min(25, score + 5);
          evidence.push('Dispute process followed — freeze/Tier 2 case mentioned per KB');
        }
        // HAP card-decline investigative questions (WI-19): card type, store, item, total cost
        if (detectedProgram === 'HAP') {
          const declineInvestigativePhrases = ["what card are you using", "shouldn't say nations", "shopping online or in person", "which store", "what were you trying to purchase", "brand, size", "total cost", "how much was"];
          const foundDeclineQ = declineInvestigativePhrases.find(p => agentTextLower.includes(p));
          if (foundDeclineQ) {
            score = Math.min(25, score + 3);
            evidence.push(`HAP card-decline investigative questions asked — positive indicator (WI-19): "${foundDeclineQ}"`);
          }
        }
        // Kaiser WI-19: agent checked actual system status before confirming rewards (positive)
        if (detectedProgram === 'Kaiser Permanente') {
          const systemCheckPhrases = ["check the actual status", "let me check the status", "checking the status of that action", "marked 'in progress'", "marked as in progress", "marked as available", "marked as completed", "the status shows", "status is showing"];
          const foundSystemCheck = systemCheckPhrases.find(p => agentTextLower.includes(p));
          if (foundSystemCheck) {
            score = Math.min(25, score + 3);
            evidence.push(`Kaiser WI-19 positive — agent verified actual system status before confirming rewards: "${foundSystemCheck}"`);
          }
        }
      }
      // KB: Medline catalog delivery timeframe
      if (intents.some(i => i.includes('medline') || i.includes('catalog'))) {
        if (agentTextLower.includes('10') && agentTextLower.includes('21') && agentTextLower.includes('business day')) {
          score = Math.min(25, score + 3);
          evidence.push('Correct Medline catalog delivery timeframe (10–21 business days per KB)');
        }
      }
      // Vague responses check
      const vagueFound = ["i'm not sure", "i don't know", 'i think so', 'i believe so'].filter(v => agentTextLower.includes(v));
      if (vagueFound.length > 1) {
        score = Math.max(0, score - 5);
        deductions.push(`Multiple vague responses detected: "${vagueFound.join('", "')}"`);
      }
      // Program-specific inaccuracy red flags
      if (detectedProgram === 'HAP') {
        if (agentTextLower.includes('outside the u.s') || agentTextLower.includes('internationally') || agentTextLower.includes('international use')) {
          score = Math.max(0, score - 8);
          deductions.push('HAP inaccuracy: card is U.S.-only (including PR, Guam, USVI) — international use not supported');
        }
        if (agentTextLower.includes('overnight') || agentTextLower.includes('rush ship') || agentTextLower.includes('expedite')) {
          score = Math.max(0, score - 5);
          deductions.push('HAP inaccuracy: catalog replacements cannot be expedited — standard mail only, 10–21 business days');
        }
        if (agentTextLower.includes('nations benefits')) {
          score = Math.max(0, score - 8);
          deductions.push('HAP inaccuracy: Nations Benefits no longer covers Flex+ purchases — only Hearing Benefits remain with Nations');
        }
        if (agentTextLower.includes('transfer') && agentTextLower.includes('reward')) {
          score = Math.max(0, score - 5);
          deductions.push('HAP inaccuracy: rewards are non-transferable to family members');
        }
        if ((agentTextLower.includes('cap') || agentTextLower.includes('maximum') || agentTextLower.includes('limit on')) && agentTextLower.includes('earn')) {
          score = Math.max(0, score - 5);
          deductions.push('HAP inaccuracy: HAP has NO earnings cap — do not confuse with Kaiser ($150/yr) or Navitus ($125/yr)');
        }
        // Only deduct when agent implies PERMANENT access — temporary/call-only authorization is correct (WI-16)
        const hapPermIntentPhrases = ['permanent', 'going forward', 'from now on', 'any time they call', 'always be able', 'future calls', 'permanently', 'on file going', 'will have access'];
        const hapImpliesPermAccess = hapPermIntentPhrases.some(p => agentTextLower.includes(p));
        if (agentTextLower.includes('authorized user') && agentTextLower.includes('add') && hapImpliesPermAccess && !agentTextLower.includes('three-way') && !agentTextLower.includes('3-way') && !agentTextLower.includes('hap customer service') && !agentTextLower.includes('call back')) {
          score = Math.max(0, score - 5);
          deductions.push('HAP inaccuracy: permanent authorized user requires a 3-way call with HAP Customer Service (313-664-8896) and a Tier 2 case — cannot be added in-call alone (WI-16)');
        }
      }
      if (detectedProgram === 'Kaiser Permanente') {
        if ((agentTextLower.includes('no limit') || agentTextLower.includes('no cap') || agentTextLower.includes('unlimited')) && agentTextLower.includes('earn')) {
          score = Math.max(0, score - 8);
          deductions.push('Kaiser inaccuracy: $150/year dollar-reward cap applies — earnings beyond that convert to drawing entries at 1:1');
        }
        // Only deduct when agent implies permanent/standing access — call-only authorization is correct (WI-16)
        const kaiserPermIntent = ['permanent', 'going forward', 'from now on', 'any time they call', 'always be able', 'future calls', 'permanently', 'on file going', 'will have access'];
        if (agentTextLower.includes('permanent authorized') || (agentTextLower.includes('authorized user') && agentTextLower.includes('add') && kaiserPermIntent.some(p => agentTextLower.includes(p)) && !agentTextLower.includes('call back') && !agentTextLower.includes('re-verify'))) {
          score = Math.max(0, score - 5);
          deductions.push('Kaiser inaccuracy: Kaiser does not support permanent authorized users — permission is call-only, every time (WI-16)');
        }
        // Agent assured reward would post without checking actual system status (WI-19)
        if ((agentTextLower.includes("that reward will post") || agentTextLower.includes("rewards will post") || agentTextLower.includes("it will post soon") || agentTextLower.includes("should post shortly")) && !agentTextLower.includes('in progress') && !agentTextLower.includes('let me check') && !agentTextLower.includes("i'm checking")) {
          score = Math.max(0, score - 5);
          deductions.push('Kaiser inaccuracy: agent assured reward would post without verifying actual system status — should confirm Available/In Progress/Completed before making a commitment (WI-19)');
        }
        // Incorrect claim that funds can be moved between wallets or purses
        if ((agentTextLower.includes('move') || agentTextLower.includes('transfer')) && (agentTextLower.includes('wallet') || agentTextLower.includes('purse')) && (agentTextLower.includes('between') || agentTextLower.includes('to your'))) {
          score = Math.max(0, score - 5);
          deductions.push('Kaiser inaccuracy: funds cannot be moved between wallets — each Wallet is managed independently per program configuration');
        }
      }
      if (detectedProgram === 'Navitus') {
        if ((agentTextLower.includes('no limit') || agentTextLower.includes('no cap') || agentTextLower.includes('unlimited')) && agentTextLower.includes('earn')) {
          score = Math.max(0, score - 8);
          deductions.push('Navitus inaccuracy: $125/year reward cap applies — earnings beyond that convert to entries at 1:1');
        }
        if ((agentTextLower.includes('transfer') || agentTextLower.includes('give')) && agentTextLower.includes('reward') && (agentTextLower.includes('family') || agentTextLower.includes('coworker') || agentTextLower.includes('someone else'))) {
          score = Math.max(0, score - 5);
          deductions.push('Navitus inaccuracy: rewards are non-transferable — cannot be given to a family member or coworker');
        }
        if ((agentTextLower.includes('move') || agentTextLower.includes('transfer')) && agentTextLower.includes('purse') && (agentTextLower.includes('between') || agentTextLower.includes('costco') || agentTextLower.includes('fitness'))) {
          score = Math.max(0, score - 5);
          deductions.push('Navitus inaccuracy: there is only ONE unified Rewards purse in 2026-2027 — no purse-to-purse transfers exist or are needed');
        }
        // Positive: agent correctly explained the single unified purse structure (Gap 5)
        const navitusPursePhrases = ['one purse', 'single purse', 'unified purse', 'one rewards purse', 'only one purse', 'one rewards account', 'single rewards account'];
        const navitusExplainedPurse = navitusPursePhrases.find(p => agentTextLower.includes(p));
        if (navitusExplainedPurse) {
          score = Math.min(25, score + 3);
          evidence.push(`Navitus single-purse structure correctly explained ("${navitusExplainedPurse}") — positive indicator`);
        }
        // Only deduct when agent implies permanent/standing access — call-only authorization is correct (WI-16)
        const navitusPermIntent = ['permanent', 'going forward', 'from now on', 'any time they call', 'always be able', 'future calls', 'permanently', 'on file going', 'will have access'];
        if (agentTextLower.includes('permanent authorized') || (agentTextLower.includes('authorized user') && agentTextLower.includes('add') && navitusPermIntent.some(p => agentTextLower.includes(p)) && !agentTextLower.includes('call back') && !agentTextLower.includes('re-verify'))) {
          score = Math.max(0, score - 5);
          deductions.push('Navitus inaccuracy: Navitus does not support permanent authorized users — permission is call-only, every time (WI-16)');
        }
        if (agentTextLower.includes('never expire') || (agentTextLower.includes('rewards') && agentTextLower.includes("don't expire")) || (agentTextLower.includes('rewards') && agentTextLower.includes('no expir'))) {
          score = Math.max(0, score - 5);
          deductions.push('Navitus inaccuracy: rewards expire 30 days after program end (12/31/2026) or 30 days after loss of eligibility');
        }
      }
      if (detectedProgram === 'Sunny Direct') {
        if (agentTextLower.includes('medical') && (agentTextLower.includes('denial') || agentTextLower.includes('denied') || agentTextLower.includes('claim') || agentTextLower.includes('coverage decision'))) {
          score = Math.max(0, score - 8);
          deductions.push('Sunny Direct: rewards agents do not discuss medical situations, denials, or claims — must transfer to Client Resources/specialist team');
        }
        if ((agentTextLower.includes("even though you're not on the line") || agentTextLower.includes("even though they're not on the line") || agentTextLower.includes('without them on the line') || agentTextLower.includes('without the member on the line')) && (agentTextLower.includes('caregiver') || agentTextLower.includes('discuss') || agentTextLower.includes('help'))) {
          score = Math.max(0, score - 8);
          deductions.push('Sunny Direct: cannot discuss account with authorized rep or caregiver without the account holder on the line — exception only for a parent of a dependent listed in the eligibility file');
        }
        // Sunny Direct positive indicators (Excel Item 6 full-credit behaviors)
        const sunnyKbRefPhrases = ['rewards & program questions', 'program questions knowledge base', 'let me look that up in our system', 'checking our knowledge base', 'let me reference that for you'];
        const sunnyKbRef = sunnyKbRefPhrases.find(p => agentTextLower.includes(p));
        if (sunnyKbRef) {
          evidence.push(`Sunny Direct: referenced KB for program/rewards question ("${sunnyKbRef}") — full credit indicator`);
        }
        const sunnyFraudProcess = agentTextLower.includes('freeze') && (agentTextLower.includes('card transactions') || agentTextLower.includes('transaction tab') || agentTextLower.includes('review the transactions') || agentTextLower.includes('unauthorized'));
        if (sunnyFraudProcess) {
          evidence.push('Sunny Direct: card frozen and transactions reviewed for unauthorized activity — full credit indicator');
        }
      }
      if (evidence.length === 0 && deductions.length === 0) {
        evidence.push('Partial assessment — transcript does not contain clearly KB-checkable statements for this call type');
      }
    }
    items.push({ section: 'Clarity, Resolution & Accuracy', criterion: 'Provided accurate and complete information', awardedPoints: Math.min(25, Math.max(0, score)), maxPoints: 25, notes: [...evidence, ...deductions].join('; ') });
  }

  // Item 7: Communicated next steps, timelines, and internal actions (25 pts)
  {
    let score = 0;
    const evidence = [];
    if (!hasTranscript) {
      evidence.push('Unable to Evaluate from Provided Materials — no transcript');
    } else {
      // Early disconnect check — auto full credit per rubric if caller disconnected before agent had opportunity
      const lastMemberText = memberLines.length > 0 ? memberLines[memberLines.length - 1].text.toLowerCase() : '';
      const disconnectPhrases = ['have to go', 'need to go', 'gotta go', 'i gotta run', 'bye', 'goodbye', 'talk later', 'ill call back', 'thanks bye', 'ok bye'];
      // Early disconnect: caller's LAST utterance is a disconnect phrase AND agent had no next-steps language yet
      const agentHadNextSteps = ['next step', "what's going to happen", "you'll receive", "we will", "i will", "i'll send", "i'll follow up"].some(p => agentTextLower.includes(p));
      const callerDisconnectedEarly = disconnectPhrases.some(p => lastMemberText.includes(p)) && !agentHadNextSteps;
      if (callerDisconnectedEarly) {
        score = 25;
        evidence.push('Automatic full credit — caller disconnected before agent had a reasonable opportunity to explain next steps (rubric: auto full credit on early disconnect)');
      } else {
        const callIntents = [call.primary_intent_a, call.primary_intent_b, call.primary_intent_c].filter(Boolean).map(i => i.toLowerCase());
        const informationalMarkers = ['general question', 'general questions', 'benefits & program', 'program question', 'benefits information', 'information only'];
        const actionMarkers = ['card replacement', 'card activation', 'dispute', 'decline', 'missing reward', 'missing card', 'fraud', 'catalog', 'medline', 'complaint', 'escalation'];
        const isInformationalCall = callIntents.some(i => informationalMarkers.some(p => i.includes(p))) && !callIntents.some(i => actionMarkers.some(p => i.includes(p)));
        if (isInformationalCall) {
          score = 20;
          evidence.push('Informational call — agent answered benefits/coverage questions with no open action item; next steps not required per WI-06; partial credit awarded');
        } else {
          const nextStepPhrases = ['i will', 'we will', "you'll", "i'll", "i'm going to", "we're going to", 'next step', 'what will happen', "what's going to happen"];
          const timelinePhrases = ['business day', 'business days', 'within', '24 hours', '48 hours', '10-13', '10 to 13'];
          const updatePhrases = ['reach out', 'contact you', 'follow up', 'get back to you', 'update you', 'let you know', 'send you'];
          // Rubric (WI-03): vague closing language = deduction even if something resembling next steps was said
          const vagueClosingPhrases = ["we'll go from there", "just give it some time", "someone will reach out eventually", "give it some time", "it should work itself out", "they'll take care of it", "it will sort itself out"];
          const foundVagueClosing = vagueClosingPhrases.find(p => agentTextLower.includes(p));
          const foundNext = nextStepPhrases.find(p => agentTextLower.includes(p));
          const foundTimeline = timelinePhrases.find(p => agentTextLower.includes(p));
          const foundUpdate = updatePhrases.find(p => agentTextLower.includes(p));
          if (foundNext) { score += 10; evidence.push(`Next step communicated: "${foundNext}"`); }
          if (foundTimeline) { score += 8; evidence.push(`Timeline provided: "${foundTimeline}"`); }
          if (foundUpdate) { score += 7; evidence.push(`Follow-up offered: "${foundUpdate}"`); }
          if (foundVagueClosing) {
            score = Math.max(0, score - 8);
            evidence.push(`Vague closing language detected: "${foundVagueClosing}" — rubric (WI-03) requires specific, actionable next steps`);
          }
          if (score === 0 && !foundVagueClosing) evidence.push('No next step, timeline, or follow-up language detected in available transcript');
        }
      }
    }
    items.push({ section: 'Clarity, Resolution & Accuracy', criterion: 'Communicated next steps, timelines, and internal actions', awardedPoints: Math.min(25, score), maxPoints: 25, notes: evidence.join('; ') });
  }

  // Item 8: Call Closing (5 pts — Pass/Fail)
  // Rubric: BOTH checking for further questions AND thanking the caller are required for full credit
  {
    let score = 0, notes;
    if (!hasTranscript) {
      notes = 'Unable to Evaluate from Provided Materials — no transcript';
    } else {
      const furtherQuestionPhrases = ['anything else', 'is there anything else', 'any other questions', 'any other concerns', 'anything else i can help', 'anything else i can assist', 'before i let you go'];
      const thankYouPhrases = ['thank you for calling', 'thanks for calling', 'thank you so much for calling', 'thank you for reaching out'];
      const askedFurtherQuestions = furtherQuestionPhrases.some(p => strictAgentText.includes(p));
      const thankedCaller = thankYouPhrases.some(p => strictAgentText.includes(p));
      // Gap 8: closing should include program name (e.g. "thank you for calling HAP" not just generic)
      const programNameInClosing = (() => {
        const lastAgentText = agentLines.slice(-5).map(l => l.text.toLowerCase()).join(' ');
        if (detectedProgram === 'HAP') return lastAgentText.includes('hap') || lastAgentText.includes('hap flex');
        if (detectedProgram === 'Kaiser Permanente') return lastAgentText.includes('kaiser') || lastAgentText.includes('healthy living');
        if (detectedProgram === 'Navitus') return lastAgentText.includes('navitus');
        if (detectedProgram === 'Sunny Direct') return lastAgentText.includes('sunny direct') || lastAgentText.includes('sunny');
        return true;
      })();
      // Item 8 is Pass/Fail: 5 or 0 only. Partial credit (3) is not in the Excel rubric.
      if (askedFurtherQuestions && thankedCaller && programNameInClosing) {
        score = 5;
        const qPhrase = furtherQuestionPhrases.find(p => strictAgentText.includes(p));
        const tPhrase = thankYouPhrases.find(p => strictAgentText.includes(p));
        notes = `Proper closing — "${qPhrase}" and program-specific "${tPhrase}"`;
      } else if (askedFurtherQuestions && thankedCaller && !programNameInClosing) {
        score = 0;
        const qPhrase = furtherQuestionPhrases.find(p => strictAgentText.includes(p));
        const tPhrase = thankYouPhrases.find(p => strictAgentText.includes(p));
        notes = `Both closing elements present but program name (${detectedProgram}) missing from thank-you — rubric requires program-specific closing (e.g., "Thank you for calling ${detectedProgram} Rewards"); scored 0 (Pass/Fail)`;
      } else if (askedFurtherQuestions && !thankedCaller) {
        score = 0;
        const qPhrase = furtherQuestionPhrases.find(p => strictAgentText.includes(p));
        notes = `Checked for further questions ("${qPhrase}") but no thank-you for calling detected — both required (Pass/Fail: scored 0)`;
      } else if (thankedCaller && !askedFurtherQuestions) {
        score = 0;
        const tPhrase = thankYouPhrases.find(p => strictAgentText.includes(p));
        notes = `Thanked caller ("${tPhrase}") but did not check for further questions — both required (Pass/Fail: scored 0)`;
      } else {
        notes = 'No closing phrases detected — rubric requires both checking for further questions and a program-specific thank-you (Pass/Fail: scored 0)';
      }
    }
    items.push({ section: 'Call Closing', criterion: 'Checked for further questions and thanked caller', awardedPoints: score, maxPoints: 5, notes });
  }

  // Item 9: Notes and Cases (10 pts — Pass/Fail per Excel rubric)
  // Primary logic: case creation verbal confirmation. Falls back to intent-field documentation when no case mentioned.
  {
    const intents = [call.primary_intent_a, call.primary_intent_b, call.primary_intent_c].filter(Boolean);
    let score, notes;
    if (hasTranscript) {
      const caseAnnounced = ["i'm going to create a case", "i will create a case", "let me open a ticket", "i'll submit a case", "i'll create a case", "creating a case for you", "i'll open a case"].some(p => agentTextLower.includes(p));
      const caseConfirmed = ["i've created that case", "i've created a case", "case has been created", "case number is", "your case number", "ticket number is", "case is now open"].some(p => agentTextLower.includes(p));
      if (caseConfirmed) {
        score = 10;
        const caseNum = agentTextLower.match(/case\s*(?:number\s*(?:is\s*)?)?([a-z0-9-]+)/i);
        notes = `Case creation confirmed${caseNum ? ` (case ref: ${caseNum[1]})` : ''} — Pass (10/10)`;
      } else if (caseAnnounced && !caseConfirmed) {
        score = 0;
        notes = 'Case announced but not confirmed — agent stated intent to create a case but never confirmed completion or provided a case number (rubric: announced-not-confirmed = 0)';
      } else {
        // No case mention — use intent fields as documentation proxy
        if (intents.length === 3) {
          score = 10;
          notes = `No case creation detected; all 3 intent fields documented (${intents.join(', ')}) — Pass (10/10)`;
        } else if (intents.length === 2) {
          score = 5;
          notes = `No case creation detected; 2 of 3 intent fields documented (${intents.join(', ')}) — partial credit (5/10)`;
        } else if (intents.length === 1) {
          score = 3;
          notes = `No case creation detected; only 1 intent field documented (${intents[0]}) — partial credit (3/10)`;
        } else {
          score = 0;
          notes = 'No case creation and no intent fields documented — Fail (0/10)';
        }
      }
    } else {
      // No transcript — rely on intent fields only
      if (intents.length === 3) {
        score = 10;
        notes = `All 3 intent fields documented: ${intents.join(', ')} — Pass (10/10; note accuracy requires Salesforce case review)`;
      } else if (intents.length === 2) {
        score = 5;
        notes = `2 of 3 intent fields documented: ${intents.join(', ')} — partial credit (5/10)`;
      } else if (intents.length === 1) {
        score = 3;
        notes = `Only 1 intent field documented: ${intents[0]} — partial credit (3/10)`;
      } else {
        score = 0;
        notes = 'No intent fields documented — Fail (0/10)';
      }
    }
    items.push({ section: 'Notes & Cases', criterion: 'Logged accurate internal notes for follow-up teams', awardedPoints: score, maxPoints: 10, notes });
  }

  // Bonus: Turned negative to positive (5 pts)
  {
    let score = 0, notes;
    if (!hasTranscript || memberLines.length < 4) {
      notes = 'Unable to Confirm — insufficient transcript to assess caller sentiment arc';
    } else {
      const negWords = ['frustrated', 'upset', 'angry', 'terrible', 'horrible', 'ridiculous', 'unacceptable', 'disappointed', 'confused', "doesn't make sense", "i don't understand"];
      const posWords = ['thank you so much', 'i appreciate', 'that helps', "that's great", 'wonderful', 'perfect', 'great job', 'very helpful'];
      const half = Math.ceil(memberLines.length / 2);
      const firstHalf = memberLines.slice(0, half).map(l => l.text.toLowerCase()).join(' ');
      const secondHalf = memberLines.slice(half).map(l => l.text.toLowerCase()).join(' ');
      const hadNeg = negWords.some(w => firstHalf.includes(w));
      const endedPos = posWords.some(w => secondHalf.includes(w));
      if (hadNeg && endedPos) {
        score = 5;
        notes = 'Caller showed frustration early and expressed satisfaction by end — bonus criteria met';
      } else if (endedPos) {
        notes = 'Caller expressed satisfaction but no initial frustration detected — bonus criteria not triggered';
      } else {
        notes = 'Unable to Confirm — no clear negative-to-positive sentiment arc in available transcript';
      }
    }
    items.push({ section: 'Bonus', criterion: 'Turned a negative or uncertain experience into a positive one', awardedPoints: score, maxPoints: 5, notes });
  }

  // Totals
  const baseScore = items.filter(i => i.section !== 'Bonus').reduce((s, i) => s + i.awardedPoints, 0);
  const bonus = items.find(i => i.section === 'Bonus')?.awardedPoints || 0;
  const rawScore = baseScore + bonus;

  const itemByKey = (kw) => items.find(i => i.criterion.toLowerCase().includes(kw.toLowerCase()));

  // Evaluation confidence (needed early for routing)
  const evaluationConfidence = allParsed.length >= 10 ? 'High' : allParsed.length >= 5 ? 'Medium' : 'Low';

  // Identity verification detection (2FA: full name + 2 of DOB/SSN-last4/account number/ZIP — Gap 12)
  const accountDiscussedPhrases = ['your balance', 'your account', 'your card', 'your rewards', 'i can see', 'i show', "i'm showing", 'on your account', 'on file'];
  const agentDiscussedAccount = accountDiscussedPhrases.some(p => agentTextLower.includes(p));
  const agentAskedName = [
    'your full name', 'your name for', 'can i get your name', 'verify your name',
    'can you verify your name', 'name on the account', 'first and last name',
    "what's your name", 'what is your name', 'your name please',
    'may i have your name', 'may i get your name', 'get your name',
    'i need your name', 'your name?', 'your name,',
    'you said your name was', 'you said your first name was',
    'your name is', 'is your name', 'i have your name as',
    'name was', 'said your name',
    'spell your first name', 'spell your last name', 'your last name',
    'first name for me', 'last name for me',
  ].some(p => agentTextLower.includes(p));
  const firstMemberText = memberLines.length > 0 ? memberLines[0].text.toLowerCase() : '';
  const memberStatedName = (
    ['my name is', 'my first name is', "name's ", "i'm calling", 'i am calling'].some(p => memberTextLower.includes(p))
    || ['i am ', "i'm ", 'this is ', 'my name'].some(p => firstMemberText.includes(p))
  );
  const nameVerified = agentAskedName || memberStatedName;
  const secondaryFactors = [
    ['date of birth', 'dob', 'birth date'].some(p => agentTextLower.includes(p)),
    ['last 4', 'last four', 'last 4 of your social', 'last four of your social', 'last 4 digits of your social'].some(p => agentTextLower.includes(p)),
    ['account number', 'member id', 'member number', 'member i.d'].some(p => agentTextLower.includes(p)),
    ['zip code', 'postal code', 'zip on file'].some(p => agentTextLower.includes(p)),
  ].filter(Boolean).length;
  const agentVerified = nameVerified && secondaryFactors >= 2;
  // Partial verification — name provided but only 1 secondary factor (incomplete 2FA)
  const agentPartiallyVerified = nameVerified && secondaryFactors === 1;
  // Third-party call detection — used for WI-16 auto-fail check
  const thirdPartyIndicators = ['third party', 'third-party', 'authorized representative', 'authorized rep', 'caregiver', 'power of attorney', 'is this the member', 'is the member available', 'speaking with the member'];
  const thirdPartyPresent = thirdPartyIndicators.some(p => fullText.includes(p)) || (memberLines.length > 0 && !memberTextLower.includes('my account') && agentTextLower.includes('on behalf'));

  // Auto-fail checks
  const autoFailTriggers = [];
  // PII read-back — agent must never read PII back to verify; caller must provide it (auto-fail per rubric)
  const piiReadbackPatterns = [
    'date of birth is', 'your dob is', 'date of birth on file is',
    'email on file is', 'email address on file is', 'email we have on file for you is', 'the email we have is',
    'your zip code is', 'zip code on file is', 'address on file is',
  ];
  const piiReadbackFound = piiReadbackPatterns.find(p => agentTextLower.includes(p));
  if (piiReadbackFound) {
    autoFailTriggers.push(`PII read-back detected — agent revealed personal information to the caller instead of having the caller provide it: phrase "${piiReadbackFound}" (auto-fail: caller must supply verification information, not the agent)`);
  }
  // Failure to verify identity before discussing account (name + 1 factor = pass; name + 0 factors = auto-fail)
  if (hasTranscript && agentDiscussedAccount && !agentVerified && !agentPartiallyVerified) {
    autoFailTriggers.push('Failure to verify identity — agent discussed account without completing identity verification (required: full name + at least 1 of: DOB / SSN-last4 / account number / ZIP)');
  }
  // Third-party without WI-16 checklist (Auto-Fail Triggers 1 & 2)
  if (hasTranscript && thirdPartyPresent) {
    const memberConfirmedOnLine = ['is the member available', 'can i speak with', 'is [member', 'member on the line', 'is the account holder available', 'member available to come to the phone', 'need him to verify', 'need her to verify', 'to verify his information', 'to verify her information', 'will need him', 'will need her'].some(p => agentTextLower.includes(p));
    const thirdPartyNameObtained = ["your full name", "your name and", "name and your relationship", "how are you related", "your relationship to", "state her name", "state his name", "state their name", "can you state"].some(p => agentTextLower.includes(p));
    const explicitPermissionObtained = ["do i have your permission", "permission to discuss", "with your permission", "you're authorizing me", "you authorize me to discuss", "verbal consent", "give verbal consent"].some(p => agentTextLower.includes(p));
    if (agentDiscussedAccount && (!memberConfirmedOnLine || !thirdPartyNameObtained || !explicitPermissionObtained)) {
      const missing = [];
      if (!memberConfirmedOnLine) missing.push('confirm member availability');
      if (!thirdPartyNameObtained) missing.push("obtain third party's name and relationship");
      if (!explicitPermissionObtained) missing.push('obtain explicit verbal permission');
      autoFailTriggers.push(`Third-party call without completing WI-16 checklist — account discussed without: ${missing.join('; ')} (auto-fail per rubric)`);
    }
  }
  // Verbal abuse or profanity (Trigger 3: any profanity or verbally abusive language directed at caller)
  const profanityPatterns = ['shut up', 'you idiot', "you're stupid", 'screw you', 'go to hell', 'what is wrong with you', 'are you kidding me', 'i could not care less'];
  if (hasTranscript && profanityPatterns.some(p => agentTextLower.includes(p))) {
    const found = profanityPatterns.find(p => agentTextLower.includes(p));
    autoFailTriggers.push(`Verbal abuse detected in agent speech: "${found}" (auto-fail Trigger 3)`);
  }
  // Rude / dismissive (Trigger 4) — Excel phrases: "That's not my problem", "I already told you that", "Ma'am, I literally just explained this", arguing back, [sigh]+curt response
  const curtResponsePatterns = ["okay. next", "is that all?", "next question", "moving on", "is that everything?"];
  const hasCurtResponse = curtResponsePatterns.some(p => agentTextLower.includes(p));
  // Clearly rude — single instance auto-fails (exact Excel Trigger 4 phrases)
  const clearlyRudePatterns = ["that's not my problem", "ma'am, i literally"];
  // Dismissive — single instance auto-fails per Excel (explicitly listed in Trigger 4)
  const dismissivePatterns = ['i already told you', 'i literally just explained'];
  const clearlyRudeFound = clearlyRudePatterns.find(p => agentTextLower.includes(p));
  const sighWithCurtResponse = agentTextLower.includes('[sigh]') && hasCurtResponse;
  const dismissiveFound = dismissivePatterns.find(p => agentTextLower.includes(p));
  const rudeCount = (clearlyRudePatterns.filter(p => agentTextLower.includes(p)).length) + (sighWithCurtResponse ? 1 : 0) + (dismissiveFound ? 1 : 0);
  if (hasTranscript && clearlyRudeFound) {
    autoFailTriggers.push(`Rude or dismissive tone — unambiguous rude language detected: "${clearlyRudeFound}" (auto-fail Trigger 4)`);
  } else if (hasTranscript && sighWithCurtResponse) {
    autoFailTriggers.push('Rude or dismissive tone — audible [sigh] paired with curt response detected (auto-fail Trigger 4: the pairing is the trigger, not [sigh] alone)');
  } else if (hasTranscript && dismissiveFound) {
    autoFailTriggers.push(`Rude or dismissive tone — dismissive language toward caller: "${dismissiveFound}" (auto-fail Trigger 4: single clear instance is sufficient)`);
  }

  const autoFailTriggered = autoFailTriggers.length > 0;
  const finalScore = autoFailTriggered ? 0 : Math.min(rawScore, 105);

  const riskLevel = autoFailTriggered ? 'Critical Risk'
    : finalScore >= 94 ? 'Low Risk'
    : finalScore >= 80 ? 'Moderate Risk'
    : finalScore >= 70 ? 'High Risk'
    : 'Critical Risk';

  // Behavior flags (computed before reviewRouting — routing depends on flags)
  const behaviorFlags = [];
  if (autoFailTriggered) behaviorFlags.push('Compliance Risk');
  if ((itemByKey('hold')?.awardedPoints || 0) === 0 && Number(call.hold_time) > 0) behaviorFlags.push('Excessive Hold or Dead Air');
  if ((itemByKey('next steps')?.awardedPoints || 0) === 0 && hasTranscript) behaviorFlags.push('Missed Next Steps Explanation');
  // Incomplete Resolution: primary issue unresolved by call end (WI-14) — tied to accuracy/next-steps, not closing item
  if (((itemByKey('accurate')?.awardedPoints || 0) < 10 || (itemByKey('next steps')?.awardedPoints || 0) === 0) && hasTranscript) behaviorFlags.push('Incomplete Resolution');
  // Tone Problem — dismissive phrases (1 instance sufficient for flag, 2+ for auto-fail)
  if (hasTranscript && rudeCount >= 1) behaviorFlags.push('Tone Problem');
  // Ownership Gap — agent deflected without taking ownership
  const ownershipGapPhrases = ["not my department", "can't help you with that", "you'll have to call", "you need to contact", "i have no control", "nothing i can do", "not something we handle", "not our department"];
  if (hasTranscript && ownershipGapPhrases.some(p => agentTextLower.includes(p))) behaviorFlags.push('Ownership Gap');
  // Escalation Needed — caller requested manager or expressed severe dissatisfaction
  const escalationPhrases = ['speak to a manager', 'speak to your manager', 'i want a supervisor', 'let me speak to a supervisor', 'this is unacceptable', 'i want to file a complaint', "i'm going to report", 'cancel my account', 'i want to cancel'];
  if (hasTranscript && escalationPhrases.some(p => memberTextLower.includes(p))) behaviorFlags.push('Escalation Needed');
  // HAP Plan-Level Escalation — member upset about benefit design/plan structure (not a service issue); Gap 9
  if (hasTranscript && detectedProgram === 'HAP') {
    const hapPlanEscalationPhrases = ["my benefit is too low", "the benefit should be", "i don't think this benefit is fair", "why don't i get more", "why is my benefit", "my plan doesn't cover", "why does my plan only", "this plan is not good", "benefits aren't worth it", "how come my plan", "i should get more benefits", "benefit amount is wrong", "my allowance is wrong", "why is my allowance"];
    if (hapPlanEscalationPhrases.some(p => memberTextLower.includes(p))) {
      behaviorFlags.push('HAP Plan-Level Escalation — member upset about benefit design or allowance amount (plan structure issue, not a service error); document and escalate per protocol');
    }
  }
  // Inaccurate Information Risk — low accuracy score with transcript present
  if ((itemByKey('accurate')?.awardedPoints || 0) < 15 && hasTranscript) behaviorFlags.push('Inaccurate Information Risk');
  // Missed Identity Verification — account discussed but 2FA not completed (full name + 2 secondary factors)
  if (hasTranscript && agentDiscussedAccount && !agentVerified) behaviorFlags.push('Missed Identity Verification — 2FA incomplete (required: full name + 2 of DOB / SSN-last4 / account number / ZIP)');
  // Poor Empathy — no empathy response detected after caller expressed frustration (Item 3 signal per Excel)
  {
    const callerFrustrationCheck = ["this is ridiculous", "i've called", "i have called", "i'm so frustrated", "i am so frustrated", "this is unacceptable", "nobody has helped me", "i'm upset", "i cannot believe", "very frustrated"].some(p => memberTextLower.includes(p));
    const agentEmpathyCheck = ["i completely understand your frustration", "i understand your frustration", "i'm sorry you're dealing with this", "i can imagine how frustrating", "i'm so sorry", "i apologize for the inconvenience", "i understand how you feel", "i'm sorry to hear", "that must be really frustrating"].some(p => agentTextLower.includes(p));
    if (hasTranscript && callerFrustrationCheck && !agentEmpathyCheck) behaviorFlags.push('Poor Empathy — caller expressed frustration but no empathy acknowledgment detected from agent before proceeding');
  }
  // Script Adherence Issue — missing opening greeting in first agent lines
  const openingGreetingPhrases = ['thank you for calling', 'thanks for calling', 'how can i assist', 'how can i help', 'how may i help', 'how may i assist'];
  const firstAgentText = agentLines.slice(0, 3).map(l => l.text.toLowerCase()).join(' ');
  if (hasTranscript && agentLines.length > 0 && !openingGreetingPhrases.some(p => firstAgentText.includes(p))) behaviorFlags.push('Script Adherence Issue');
  // Call Control Issue — member dominated by volume OR Item 1 scored 0 due to confirmed interruption pattern (Gap 10)
  const item1Score = itemByKey('fully explain')?.awardedPoints ?? null;
  if (hasTranscript && (memberLines.length > agentLines.length * 2.5 && memberLines.length > 8)) behaviorFlags.push('Call Control Issue — member dominated the call');
  if (hasTranscript && item1Score === 0 && !(memberLines.length > agentLines.length * 2.5 && memberLines.length > 8)) behaviorFlags.push('Call Control Issue — agent interruption pattern confirmed (Item 1 scored 0)');
  // Sunny Disclosure Violation
  if (hasTranscript && detectedProgram === 'Kaiser Permanente' && /\bsunny\b/.test(agentTextLower)) behaviorFlags.push('Sunny Disclosure Violation — "Sunny" mentioned on Kaiser Permanente call (never permitted under any circumstance)');
  if (hasTranscript && detectedProgram === 'HAP' && /\bsunny\b/.test(agentTextLower) && !/\bsunny\b/.test(memberTextLower)) behaviorFlags.push('Sunny Disclosure Violation — "Sunny" mentioned unprompted on HAP call (only permitted if Member asks first)');
  // Navitus: Sunny permitted only when discussing the Sunny app
  if (hasTranscript && detectedProgram === 'Navitus' && /\bsunny\b/.test(agentTextLower) && !agentTextLower.includes('sunny application') && !agentTextLower.includes('sunny app') && !agentTextLower.includes('sunny payments & benefits')) behaviorFlags.push('Sunny Disclosure Violation — "Sunny" referenced outside the app context on a Navitus call (only permitted when discussing the Sunny Payments & Benefits Application)');
  // Wrong program terminology
  if (hasTranscript && detectedProgram === 'Kaiser Permanente' && agentTextLower.includes('purse') && !agentTextLower.includes('wallet')) behaviorFlags.push('Wrong Terminology — used "Purse" on Kaiser Permanente call (should be "Wallet")');
  if (hasTranscript && detectedProgram === 'HAP' && agentTextLower.includes(' wallet') && !agentTextLower.includes('purse')) behaviorFlags.push('Wrong Terminology — used "Wallet" on HAP call (should be "Purse")');
  if (hasTranscript && detectedProgram === 'Navitus' && agentTextLower.includes(' member ') && !agentTextLower.includes('associate')) behaviorFlags.push('Wrong Terminology — used "Member" on Navitus call (should be "Associate")');
  // Reward cap misinformation
  if (hasTranscript && detectedProgram === 'HAP' && (agentTextLower.includes('cap') || agentTextLower.includes('maximum') || agentTextLower.includes('limit on')) && agentTextLower.includes('earn')) behaviorFlags.push('Reward Cap Misinformation — HAP has no earnings cap');
  if (hasTranscript && detectedProgram === 'Kaiser Permanente' && (agentTextLower.includes('no limit') || agentTextLower.includes('no cap') || agentTextLower.includes('unlimited')) && agentTextLower.includes('earn')) behaviorFlags.push('Reward Cap Misinformation — Kaiser $150/year cap applies');
  if (hasTranscript && detectedProgram === 'Navitus' && (agentTextLower.includes('no limit') || agentTextLower.includes('no cap') || agentTextLower.includes('unlimited')) && agentTextLower.includes('earn')) behaviorFlags.push('Reward Cap Misinformation — Navitus $125/year cap applies');
  // Authorized user mishandled
  if (hasTranscript && detectedProgram === 'Kaiser Permanente' && agentTextLower.includes('permanent authorized')) behaviorFlags.push('Authorized User Mishandled — Kaiser has no permanent authorized user; call-only permission only');
  if (hasTranscript && detectedProgram === 'HAP' && agentTextLower.includes('authorized user') && agentTextLower.includes('add') && ['permanent', 'going forward', 'from now on', 'any time they call', 'always be able', 'future calls', 'permanently', 'on file going', 'will have access'].some(p => agentTextLower.includes(p)) && !agentTextLower.includes('three-way') && !agentTextLower.includes('3-way') && !agentTextLower.includes('call back')) behaviorFlags.push('Authorized User Mishandled — HAP permanent authorized user requires 3-way call with HAP Customer Service');
  if (hasTranscript && detectedProgram === 'Navitus' && agentTextLower.includes('permanent authorized')) behaviorFlags.push('Authorized User Mishandled — Navitus has no permanent authorized user; permission is call-only, every time (WI-16)');
  // Implied permanent authorized user — softer language that doesn't say "permanent" but implies standing access (Kaiser/Navitus only — WI-16)
  const impliedPermAccessPhrases = ['carries over for next time', 'carry over for next time', 'next time without you', 'speak with them again in the future without you', 'without you being on the line next time', 'note this for future calls', 'note this so we can speak with them again'];
  if (hasTranscript && (detectedProgram === 'Kaiser Permanente' || detectedProgram === 'Navitus') && impliedPermAccessPhrases.some(p => agentTextLower.includes(p))) {
    behaviorFlags.push(`Authorized User Mishandled — implied standing/future access for ${detectedProgram}; authorization must be re-confirmed on every call; no permanent authorized user exists (WI-16)`);
  }
  // Case announced but not confirmed
  if (hasTranscript) {
    const caseAnnouncedFlag = ["i'm going to create a case", "i will create a case", "i'll submit a case", "i'll create a case", "creating a case"].some(p => agentTextLower.includes(p));
    const caseConfirmedFlag = ["i've created that case", "i've created a case", "case has been created", "case number is", "your case number"].some(p => agentTextLower.includes(p));
    if (caseAnnouncedFlag && !caseConfirmedFlag) behaviorFlags.push('Case Announced Not Confirmed — agent said they would create a case but never confirmed completion');
  }

  // Review Routing — flag and condition based per rubric (not score-based)
  let reviewRouting;
  if (autoFailTriggered) {
    reviewRouting = 'Human Review Required';
  } else if (evaluationConfidence === 'Low' || finalScore < 70) {
    reviewRouting = 'Human Review Required';
  } else if (behaviorFlags.some(f => f === 'Compliance Risk' || f.startsWith('Missed Identity Verification'))) {
    reviewRouting = 'Compliance Review';
  } else if (behaviorFlags.some(f => ['Tone Problem', 'Ownership Gap', 'Escalation Needed'].includes(f))) {
    reviewRouting = 'Supervisor Review';
  } else {
    reviewRouting = 'Standard QA';
  }

  // Strengths
  const strengths = [];
  if ((itemByKey('confidence')?.awardedPoints || 0) === 10) strengths.push('Strong delivery confidence — minimal filler words');
  if ((itemByKey('further questions')?.awardedPoints || 0) === 5) strengths.push('Proper call closing — checked for further questions and thanked caller');
  if ((itemByKey('next steps')?.awardedPoints || 0) >= 20) strengths.push('Clear next steps and timelines communicated to caller');
  if ((itemByKey('hold')?.awardedPoints || 0) === 10) strengths.push('Hold time managed appropriately');
  if ((itemByKey('notes')?.awardedPoints || 0) >= 7) strengths.push('Complete intent documentation across all fields');
  if ((itemByKey('accurate')?.awardedPoints || 0) === 25) strengths.push('Accurate and complete information provided to caller');
  if ((itemByKey('acknowledged')?.awardedPoints || 0) === 5) strengths.push('Strong caller acknowledgment and personalization');

  // Opportunities with failure types
  const opportunities = [];
  if ((itemByKey('confidence')?.awardedPoints || 0) < 10)
    opportunities.push({ text: 'Reduce filler words (um, uh) to improve perceived confidence', failureType: 'Communication skill gap' });
  if ((itemByKey('further questions')?.awardedPoints || 0) < 5)
    opportunities.push({ text: 'Ensure closing includes "anything else" and thank you to caller', failureType: 'Process gap' });
  if ((itemByKey('next steps')?.awardedPoints || 0) < 15)
    opportunities.push({ text: 'Communicate clear next steps and expected timelines to caller', failureType: 'Communication skill gap' });
  if ((itemByKey('hold')?.awardedPoints || 0) < 10)
    opportunities.push({ text: 'Always ask permission and explain reason before placing caller on hold', failureType: 'Process gap' });
  if ((itemByKey('accurate')?.awardedPoints || 0) < 20)
    opportunities.push({ text: 'Verify accuracy of information provided against the Knowledge Base', failureType: 'Knowledge gap' });
  if (behaviorFlags.some(f => f.startsWith('Missed Identity Verification')))
    opportunities.push({ text: 'Complete 2FA before discussing account: full name + 2 of DOB / SSN-last4 / account number / ZIP', failureType: 'Compliance gap' });

  // Issue categories from intent fields
  const mapIntentToCategory = (intent) => {
    if (!intent) return null;
    const i = intent.toLowerCase();
    if (i.includes('card activation') || i.includes('activate') || i.includes('activation')) return 'Card Activation';
    if (i.includes('missing card') || i.includes('lost card') || i.includes('never arrived') || i.includes('replacement') || i.includes('new card') || i.includes('stolen')) return 'Missing Card';
    if (i.includes('decline') || i.includes('declined') || i.includes('dispute') || i.includes('fraud')) return 'Decline';
    if (i.includes('missing benefit') || i.includes('medline') || i.includes('catalog') || i.includes('otc')) return 'Missing Benefits';
    if (i.includes('reward') || i.includes('rewards') || i.includes('missing reward')) return 'Missing Rewards';
    if (i.includes('technical') || i.includes('app') || i.includes('website') || i.includes('portal')) return 'Technical Support';
    if (i.includes('login') || i.includes('password') || i.includes('locked') || i.includes('account access') || i.includes('account') || i.includes('access')) return 'Account Access';
    if (i.includes('enroll') || i.includes('plan change') || i.includes('disenroll')) return 'Enrollment or Plan Changes';
    if (i.includes('general') || i.includes('question') || i.includes('benefit') || i.includes('benefits') || i.includes('information')) return 'General Benefits Question';
    return 'Other';
  };
  const primaryIssueCategory = mapIntentToCategory(call.primary_intent_a) || 'General Benefits Question';
  const secondaryIssueCategory = mapIntentToCategory(call.primary_intent_b);

  // Risk reason
  const riskReason = autoFailTriggered
    ? `Auto-fail triggered — ${autoFailTriggers[0]}`
    : finalScore >= 94 ? 'Strong performance across all evaluated criteria'
    : finalScore >= 80 ? `Score of ${finalScore}/105 — coaching opportunities identified; no critical compliance concerns`
    : finalScore >= 70 ? `Score of ${finalScore}/105 — performance gaps require supervisor review`
    : `Score of ${finalScore}/105 — critical performance gaps across multiple rubric sections`;

  // Recommended training
  const recommendedTraining = [];
  if ((itemByKey('confidence')?.awardedPoints || 0) < 10) recommendedTraining.push('Confident Delivery and Filler Word Reduction');
  if ((itemByKey('further questions')?.awardedPoints || 0) < 5) recommendedTraining.push('Call Closing Techniques');
  if ((itemByKey('next steps')?.awardedPoints || 0) < 15) recommendedTraining.push('Next Steps Communication and Timeline Setting');
  if ((itemByKey('hold')?.awardedPoints || 0) < 10) recommendedTraining.push('Hold Management and Dead Air Prevention');
  if ((itemByKey('accurate')?.awardedPoints || 0) < 20) recommendedTraining.push('Product Knowledge and Knowledge Base Accuracy');
  if ((itemByKey('notes')?.awardedPoints || 0) < 7) recommendedTraining.push('Documentation and Case Management');
  if (autoFailTriggered) recommendedTraining.push('HIPAA and Privacy Compliance');

  // Disposition recommendations
  const needsSupervisorFollowUp = behaviorFlags.includes('Tone Problem') || behaviorFlags.includes('Ownership Gap') || behaviorFlags.includes('Escalation Needed');
  const dispositionRecommendations = [];
  if (autoFailTriggered) {
    dispositionRecommendations.push('Compliance Review');
    dispositionRecommendations.push('Supervisor Follow-Up');
  } else if (finalScore >= 94) {
    // No disposition needed
  } else if (finalScore >= 80) {
    dispositionRecommendations.push('Coaching Only');
    if (needsSupervisorFollowUp) dispositionRecommendations.push('Supervisor Follow-Up');
    if ((itemByKey('accurate')?.awardedPoints || 0) < 20) dispositionRecommendations.push('Knowledge Article Review Needed');
    if ((itemByKey('next steps')?.awardedPoints || 0) < 15) dispositionRecommendations.push('Process Clarification Needed');
  } else if (finalScore >= 70) {
    dispositionRecommendations.push('QA Review');
    if (needsSupervisorFollowUp) dispositionRecommendations.push('Supervisor Follow-Up');
    if ((itemByKey('accurate')?.awardedPoints || 0) < 20) dispositionRecommendations.push('Knowledge Article Review Needed');
    if ((itemByKey('next steps')?.awardedPoints || 0) < 15) dispositionRecommendations.push('Process Clarification Needed');
  } else {
    dispositionRecommendations.push('QA Review');
    if (needsSupervisorFollowUp) dispositionRecommendations.push('Supervisor Follow-Up');
    if ((itemByKey('accurate')?.awardedPoints || 0) < 20) dispositionRecommendations.push('Knowledge Article Review Needed');
    if ((itemByKey('next steps')?.awardedPoints || 0) < 15) dispositionRecommendations.push('Process Clarification Needed');
  }

  // Evidence notes (only for low scores or auto-fail)
  const evidenceNotes = [];
  if (finalScore < 70 || autoFailTriggered) {
    const sortedByPerf = [...items]
      .filter(item => item.maxPoints > 0 && item.awardedPoints < item.maxPoints && item.notes)
      .sort((a, b) => (a.awardedPoints / a.maxPoints) - (b.awardedPoints / b.maxPoints));
    sortedByPerf.slice(0, 4).forEach(item => {
      evidenceNotes.push(`${item.criterion} (${item.awardedPoints}/${item.maxPoints}): ${item.notes}`);
    });
  }

  return {
    detectedProgram,
    finalScore,
    baseScore,
    bonus,
    rawScore: baseScore + bonus,
    autoFailTriggered,
    autoFailReason: autoFailTriggers[0] || null,
    riskLevel,
    riskReason,
    reviewRouting,
    primaryIssueCategory,
    secondaryIssueCategory,
    items,
    strengths: strengths.slice(0, 3),
    opportunities: opportunities.slice(0, 3),
    behaviorFlags,
    recommendedTraining,
    dispositionRecommendations,
    evidenceNotes,
    evaluatedAt: new Date().toISOString(),
    evaluationConfidence,
    evaluationLimitations: [
      ...(evaluationConfidence === 'Low' ? ['Short transcript — fewer than 5 parsed lines; items 1, 3, 5 may have limited evidence'] : []),
      'Tone assessment (Item 3) scored from text-based indicators — audio review required to confirm actual vocal tone',
      'Note accuracy (Item 9) partially scored from intent fields — full accuracy review requires Salesforce case access',
    ],
  };
};


function computeTrend(agentName, history) {
  const evals = (history[agentName] || []);
  if (evals.length < 2) return null;

  const count = evals.length;
  const avgScore = Math.round(evals.reduce((s, e) => s + Number(e.finalScore || 0), 0) / count);
  const autoFailCount = evals.filter(e => e.autoFailTriggered).length;

  const issueCounts = {};
  const failureTypeCounts = {};
  const strengthCounts = {};
  evals.forEach(e => {
    (e.opportunities || []).forEach(o => {
      const text = typeof o === 'string' ? o : o.text;
      const ft = typeof o === 'string' ? null : o.failureType;
      if (text) issueCounts[text] = (issueCounts[text] || 0) + 1;
      if (ft) failureTypeCounts[ft] = (failureTypeCounts[ft] || 0) + 1;
    });
    (e.strengths || []).forEach(s => {
      if (s) strengthCounts[s] = (strengthCounts[s] || 0) + 1;
    });
  });

  const commonIssues = Object.entries(issueCounts)
    .filter(([, c]) => c >= 2).sort((a, b) => b[1] - a[1]).map(([t]) => t).slice(0, 3);
  const commonFailureTypes = Object.entries(failureTypeCounts)
    .filter(([, c]) => c >= 2).sort((a, b) => b[1] - a[1]).map(([ft]) => ft).slice(0, 2);
  const commonStrengths = Object.entries(strengthCounts)
    .filter(([, c]) => c >= 2).sort((a, b) => b[1] - a[1]).map(([s]) => s).slice(0, 3);

  const scores = evals.map(e => e.finalScore);
  const last = scores[scores.length - 1];
  const prev = scores[scores.length - 2];
  const trend = last > prev ? 'improving' : last < prev ? 'declining' : 'stable';

  let recurringIssueCategories = [], recurringBehaviorFlags = [];
  if (count >= 5) {
    const catCounts = {}, flagCounts = {};
    evals.forEach(e => {
      if (e.primaryIssueCategory) catCounts[e.primaryIssueCategory] = (catCounts[e.primaryIssueCategory] || 0) + 1;
      (e.behaviorFlags || []).forEach(f => { flagCounts[f] = (flagCounts[f] || 0) + 1; });
    });
    recurringIssueCategories = Object.entries(catCounts).filter(([, c]) => c >= 3).sort((a, b) => b[1] - a[1]).map(([cat]) => cat);
    recurringBehaviorFlags = Object.entries(flagCounts).filter(([, c]) => c >= 3).sort((a, b) => b[1] - a[1]).map(([f]) => f);
  }

  let recommendedAction;
  if (autoFailCount > 0) recommendedAction = 'Compliance Review Required';
  else if (avgScore < 60) recommendedAction = 'Supervisor Follow-Up Recommended';
  else if (avgScore < 80) recommendedAction = 'Targeted Coaching Recommended';
  else recommendedAction = 'Monitor Progress';

  return { count, avgScore, autoFailCount, commonIssues, commonFailureTypes, commonStrengths, trend, recurringIssueCategories, recurringBehaviorFlags, recommendedAction };
}

const RISK_PILL = {
  'Low Risk': 'bg-emerald-100 text-emerald-700',
  'Moderate Risk': 'bg-amber-100 text-amber-700',
  'High Risk': 'bg-amber-100 text-amber-800',
  'Critical Risk': 'bg-red-100 text-red-700',
};

const ROUTING_PILL = {
  'Standard QA': 'bg-gray-100 text-gray-600',
  'Supervisor Review': 'bg-amber-100 text-amber-700',
  'Compliance Review': 'bg-amber-100 text-amber-700',
  'Human Review Required': 'bg-red-100 text-red-700',
};

const SECTION_HEADER = {
  'General Experience': 'bg-gray-50 border-gray-200 text-gray-700',
  'Clarity, Resolution & Accuracy': 'bg-gray-50 border-gray-200 text-gray-700',
  'Call Closing': 'bg-gray-50 border-gray-200 text-gray-700',
  'Notes & Cases': 'bg-gray-50 border-gray-200 text-gray-700',
  'Bonus': 'bg-amber-50 border-amber-200 text-amber-700',
};

const scoreBarColor = (pct) =>
  pct >= 90 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-400' : 'bg-red-500';

function ScoreCircle({ score }) {
  const r = 48;
  const circ = 2 * Math.PI * r;
  const dash = Math.min(1, (score || 0) / 105) * circ;
  const [ring, bg, stroke] =
    score >= 94 ? ['text-emerald-600', 'bg-emerald-50', 'stroke-emerald-500']
    : score >= 70 ? ['text-amber-600', 'bg-amber-50', 'stroke-amber-400']
    : ['text-red-600', 'bg-red-50', 'stroke-red-500'];

  return (
    <div className={`relative w-32 h-32 flex items-center justify-center rounded-full ${bg} flex-shrink-0`}>
      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#e5e7eb" strokeWidth="10" />
        <circle cx="60" cy="60" r={r} fill="none" className={stroke} strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <div className="text-center z-10">
        <div className={`text-3xl font-bold ${ring}`}>{score ?? 0}</div>
        <div className="text-xs text-gray-400">/105</div>
      </div>
    </div>
  );
}

function EditableScoreInput({ value, max, onChange, edited }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = React.useRef(null);

  const commit = () => {
    const n = Math.min(max, Math.max(0, Number(draft)));
    if (!isNaN(n) && n !== value) onChange(n);
    else setDraft(String(value));
    setEditing(false);
  };

  React.useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);
  React.useEffect(() => { if (!editing) setDraft(String(value)); }, [value, editing]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={0}
        max={max}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(String(value)); setEditing(false); } }}
        className="w-12 text-center border border-amber-400 rounded px-1 py-0.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-300"
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      title="Click to edit score"
      className={`flex items-center gap-1 text-sm font-semibold tabular-nums rounded px-1.5 py-0.5 transition-colors ${
        edited ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300' : 'text-gray-900 hover:bg-gray-100'
      }`}
    >
      {value}/{max}
      <Edit2 className="w-3 h-3 text-gray-400" />
    </button>
  );
}

function SectionCard({ section, items, onScoreChange, editedCriteria }) {
  const [open, setOpen] = useState(true);
  const awarded = items.reduce((s, i) => s + (i.awardedPoints || 0), 0);
  const max = items.reduce((s, i) => s + (i.maxPoints || 0), 0);
  const headerCls = SECTION_HEADER[section] || 'bg-gray-50 border-gray-200 text-gray-700';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-3 border-b ${headerCls}`}
      >
        <span className="font-semibold text-sm">{section}</span>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold tabular-nums">{awarded}/{max}</span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>
      {open && (
        <div className="divide-y divide-gray-50">
          {items.map((item, i) => {
            const ip = item.maxPoints > 0 ? Math.round((item.awardedPoints / item.maxPoints) * 100) : 0;
            const isEdited = editedCriteria?.has(item.criterion);
            return (
              <div key={i} className={`px-4 py-3 ${isEdited ? 'bg-amber-50' : ''}`}>
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <span className="text-sm text-gray-700 font-medium">{item.criterion}</span>
                  <EditableScoreInput
                    value={item.awardedPoints}
                    max={item.maxPoints}
                    edited={isEdited}
                    onChange={newScore => onScoreChange(item.criterion, newScore)}
                  />
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-1.5">
                  <div className={`h-full rounded-full ${scoreBarColor(ip)}`} style={{ width: `${ip}%` }} />
                </div>
                {item.notes && <p className="text-xs text-gray-500 leading-relaxed">{item.notes}</p>}
                {isEdited && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><Edit2 className="w-3 h-3" />Manually adjusted</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function QACallEvaluation() {
  const navigate = useNavigate();
  const location = useLocation();
  const _urlParams = new URLSearchParams(location.search);
  const _callKey = _urlParams.get('callKey') || '';
  const [filters, setFilters] = useState({
    agentName: _urlParams.get('agent') || '',
    dateFrom: '',
    dateTo: '',
    status: _urlParams.get('status') || 'all',
    autoFailOnly: false,
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterPanelRef = React.useRef(null);
  const [agentSuggestions, setAgentSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const agentSuggestTimer = React.useRef(null);
  const [calls, setCalls] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 25 });
  const [listLoading, setListLoading] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptState, setTranscriptState] = useState('idle'); // idle | loading | loaded | failed
  const [manualInput, setManualInput] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalError, setEvalError] = useState(null);
  const [activeTab, setActiveTab] = useState('transcript');
  const [showMaximized, setShowMaximized] = useState(false);
  const [evaluationScores, setEvaluationScores] = useState({});
  const [evaluationHistory, setEvaluationHistory] = useState({});
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideScore, setOverrideScore] = useState('');
  const [editableItems, setEditableItems] = useState(null);
  const [editedCriteria, setEditedCriteria] = useState(new Set());
  const [autoFailDismissed, setAutoFailDismissed] = useState(false);
  const [isEvaluationSaved, setIsEvaluationSaved] = useState(false);
  const [showTrendModal, setShowTrendModal] = useState(false);
  const [trendAgents, setTrendAgents] = useState([]);
  const [trendAgentsLoading, setTrendAgentsLoading] = useState(false);
  const [trendAgentSearch, setTrendAgentSearch] = useState('');
  const [selectedTrendAgent, setSelectedTrendAgent] = useState(null);
  const [trendActiveTab, setTrendActiveTab] = useState('aggregated');
  const [agentAggregatedData, setAgentAggregatedData] = useState(null);
  const [agentAggregatedLoading, setAgentAggregatedLoading] = useState(false);
  const [agentWeekData, setAgentWeekData] = useState(null);
  const [agentWeekLoading, setAgentWeekLoading] = useState(false);

  const fetchCalls = useCallback(async (page = 1) => {
    setListLoading(true);
    try {
      const params = { page, limit: pagination.limit };
      if (_callKey) params.vendorCallKey = _callKey;
      if (filters.agentName.trim()) params.agentName = filters.agentName.trim();
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.autoFailOnly) params.autoFailOnly = true;
      const res = await api.get('/reports/call-center/qa/calls', { params });
      const rows = res.data || [];
      setCalls(rows);
      setPagination(p => ({ ...p, page, total: res.total || 0 }));
      if (_callKey && rows.length === 1) setSelectedCall(rows[0]);
    } catch (err) {
      console.error('QA call list fetch failed:', err);
    } finally {
      setListLoading(false);
    }
  }, [filters, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCalls(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
        setShowFilterPanel(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClearFilters = useCallback(() => {
    const cleared = { agentName: '', dateFrom: '', dateTo: '', status: 'all', autoFailOnly: false };
    setFilters(cleared);
    // fetch with cleared filters directly rather than relying on state update
    setListLoading(true);
    api.get('/reports/call-center/qa/calls', { params: { page: 1, limit: 25 } })
      .then(res => {
        setCalls(res.data || []);
        setPagination(p => ({ ...p, page: 1, total: res.total || 0 }));
      })
      .catch(err => console.error(err))
      .finally(() => setListLoading(false));
  }, []);

  const loadPreScoredReport = useCallback((callId, agentName, qaScore, fullReportJson, isOverridden) => {
    try {
      const report = typeof fullReportJson === 'string' ? JSON.parse(fullReportJson) : fullReportJson;
      const enriched = {
        evaluationLimitations: [],
        riskReason: null,
        primaryIssueCategory: null,
        recommendedTraining: [],
        dispositionRecommendations: [],
        evidenceNotes: [],
        ...report,
        finalScore: qaScore ?? report.finalScore,
      };
      setEvaluation(enriched);
      setEditableItems((report.items || []).map(item => ({ ...item })));
      setEditedCriteria(new Set());
      setIsEvaluationSaved(true);
      if (isOverridden) setAutoFailDismissed(true);
      setEvaluationScores(prev => ({ ...prev, [callId]: Math.round(Number(enriched.finalScore)) }));
      const agentKey = agentName || 'Unknown';
      setEvaluationHistory(prev => {
        const existing = (prev[agentKey] || []).filter(e => e.callId !== callId);
        return {
          ...prev,
          [agentKey]: [...existing, {
            callId,
            finalScore: enriched.finalScore,
            autoFailTriggered: enriched.autoFailTriggered,
            opportunities: enriched.opportunities || [],
            strengths: enriched.strengths || [],
            primaryIssueCategory: enriched.primaryIssueCategory,
            behaviorFlags: enriched.behaviorFlags || [],
          }],
        };
      });
    } catch (e) {
      console.warn('Could not parse full_report_json:', e);
    }
  }, []);

  const selectCall = useCallback(async (call) => {
    setSelectedCall(call);
    setTranscriptText('');
    setManualInput('');
    setEvaluation(null);
    setEvalError(null);
    setActiveTab(call.qa_score != null ? 'scorecard' : 'transcript');
    setShowMaximized(true);
    setSaveState('idle');
    setOverrideMode(false);
    setOverrideScore('');
    setAutoFailDismissed(false);
    setEditableItems(null);
    setEditedCriteria(new Set());
    setIsEvaluationSaved(false);

    if (!call.conversation_identifier) {
      setTranscriptState('failed');
      if (call.qa_score != null) {
        try {
          const detailRes = await api.get(`/reports/call-center/qa/calls/${call.id}`);
          const d = detailRes.data;
          if (d?.full_report_json) {
            loadPreScoredReport(call.id, call.agent_name, call.qa_score, d.full_report_json, d.is_overridden);
          }
        } catch {}
      }
      return;
    }

    setTranscriptState('loading');
    try {
      const res = await api.get(`/reports/call-center/qa/calls/${call.id}/transcript`);
      const text = res.data?.transcript;
      if (text && text.trim()) {
        setTranscriptText(text);
        setTranscriptState('loaded');
      } else {
        setTranscriptState('failed');
      }
      const callDetail = res.data?.call;
      if (callDetail?.full_report_json) {
        loadPreScoredReport(call.id, call.agent_name, callDetail.qa_score ?? call.qa_score, callDetail.full_report_json, callDetail.is_overridden);
      }
    } catch {
      setTranscriptState('failed');
    }
  }, [loadPreScoredReport]);

  const handleEvaluate = useCallback(async () => {
    if (!selectedCall) return;
    setEvalLoading(true);
    setEvalError(null);
    try {
      const transcript = transcriptState === 'loaded' ? transcriptText : manualInput.trim();
      const result = computeRuleBasedEvaluation(selectedCall, transcript);
      setEvaluation(result);
      setAutoFailDismissed(false);
      setEditableItems(result.items.map(item => ({ ...item })));
      setEditedCriteria(new Set());
      setIsEvaluationSaved(false);
      setEvaluationScores(prev => ({ ...prev, [selectedCall.id]: result.finalScore }));
      const agentKey = selectedCall.agent_name || 'Unknown';
      setEvaluationHistory(prev => {
        const existing = (prev[agentKey] || []).filter(e => e.callId !== selectedCall.id);
        return {
          ...prev,
          [agentKey]: [...existing, {
            callId: selectedCall.id,
            finalScore: result.finalScore,
            autoFailTriggered: result.autoFailTriggered,
            opportunities: result.opportunities,
            strengths: result.strengths,
            primaryIssueCategory: result.primaryIssueCategory,
            behaviorFlags: result.behaviorFlags,
          }],
        };
      });
      setActiveTab('scorecard');
      // Auto-save immediately after evaluate
      setSaveState('saving');
      try {
        const baseScore = result.items.filter(i => i.section !== 'Bonus').reduce((s, i) => s + (i.awardedPoints || 0), 0);
        const bonus = result.items.find(i => i.section === 'Bonus')?.awardedPoints || 0;
        const finalScore = (result.autoFailTriggered && !autoFailDismissed) ? 0 : Math.min(baseScore + bonus, 105);
        const payload = {
          vendorCallKey: selectedCall.vendor_call_key,
          interactionId: selectedCall.interaction_id,
          conversationId: selectedCall.conversation_id,
          conversationIdentifier: selectedCall.conversation_identifier,
          agentName: selectedCall.agent_name,
          callDate: selectedCall.interaction_date,
          handleTimeSec: selectedCall.handle_time,
          holdTimeSec: selectedCall.hold_time,
          talkTimeSec: selectedCall.talk_time,
          qaScore: finalScore,
          maxScore: 105,
          scorePct: Number(((finalScore / 105) * 100).toFixed(2)),
          autoFail: result.autoFailTriggered && !autoFailDismissed,
          autoFailReason: result.autoFailReason || null,
          pipelineVersion: '1.0',
          fullReportJson: {
            items: result.items,
            behaviorFlags: result.behaviorFlags,
            strengths: result.strengths,
            opportunities: result.opportunities,
            editedCriteria: [],
            autoFailDismissed: autoFailDismissed || false,
            autoFailTriggered: result.autoFailTriggered || false,
            autoFailReason: result.autoFailReason || null,
          },
          isOverridden: autoFailDismissed,
          overriddenBy: autoFailDismissed ? 'QA Reviewer' : null,
        };
        await api.post(`/reports/call-center/qa/calls/${selectedCall.id}/evaluation/save`, payload);
        setSaveState('saved');
        setIsEvaluationSaved(true);
      } catch {
        setSaveState('error');
      }
    } catch (err) {
      setEvalError('Evaluation failed. Please try again.');
    } finally {
      setEvalLoading(false);
    }
  }, [selectedCall, transcriptState, transcriptText, manualInput]);

  const handleItemScoreChange = useCallback((criterion, newScore) => {
    setEditableItems(prev => prev.map(item =>
      item.criterion === criterion ? { ...item, awardedPoints: newScore } : item
    ));
    setEditedCriteria(prev => new Set([...prev, criterion]));
    setSaveState('idle');
  }, []);

  const handleSaveEvaluation = useCallback(async (isOverride = false) => {
    if (!selectedCall || !evaluation) return;
    setSaveState('saving');
    try {
      const items = editableItems || evaluation.items;
      const baseScore = items.filter(i => i.section !== 'Bonus').reduce((s, i) => s + (i.awardedPoints || 0), 0);
      const bonus = items.find(i => i.section === 'Bonus')?.awardedPoints || 0;
      const computedScore = (evaluation.autoFailTriggered && !autoFailDismissed) ? 0 : Math.min(baseScore + bonus, 105);
      const hasEdits = editedCriteria.size > 0;
      const finalScore = computedScore;
      const payload = {
        vendorCallKey: selectedCall.vendor_call_key,
        interactionId: selectedCall.interaction_id,
        conversationId: selectedCall.conversation_id,
        conversationIdentifier: selectedCall.conversation_identifier,
        agentName: selectedCall.agent_name,
        callDate: selectedCall.interaction_date,
        handleTimeSec: selectedCall.handle_time,
        holdTimeSec: selectedCall.hold_time,
        talkTimeSec: selectedCall.talk_time,
        qaScore: finalScore,
        maxScore: 105,
        scorePct: Number(((finalScore / 105) * 100).toFixed(2)),
        autoFail: evaluation.autoFailTriggered && !autoFailDismissed,
        autoFailReason: evaluation.autoFailReason || null,
        pipelineVersion: '1.0',
        fullReportJson: {
          items,
          behaviorFlags: evaluation.behaviorFlags,
          strengths: evaluation.strengths,
          opportunities: evaluation.opportunities,
          editedCriteria: hasEdits ? [...editedCriteria] : [],
          autoFailDismissed: autoFailDismissed || false,
          autoFailTriggered: evaluation.autoFailTriggered || false,
          autoFailReason: evaluation.autoFailReason || null,
        },
        isOverridden: hasEdits || autoFailDismissed,
        overriddenBy: (hasEdits || autoFailDismissed) ? 'QA Reviewer' : null,
      };
      await api.post(`/reports/call-center/qa/calls/${selectedCall.id}/evaluation/save`, payload);
      setSaveState('saved');
      setEditedCriteria(new Set());
      setIsEvaluationSaved(true);
      setEvaluationScores(prev => ({ ...prev, [selectedCall.id]: finalScore }));
    } catch {
      setSaveState('error');
    }
  }, [selectedCall, evaluation, overrideScore, editableItems, editedCriteria, autoFailDismissed]);

  const openTrendModal = useCallback(async () => {
    setShowTrendModal(true);
    setSelectedTrendAgent(null);
    setTrendAgentSearch('');
    setAgentAggregatedData(null);
    setTrendAgentsLoading(true);
    try {
      const res = await api.get('/reports/call-center/qa/agent-trends');
      setTrendAgents(res.data || []);
    } catch {
      setTrendAgents([]);
    } finally {
      setTrendAgentsLoading(false);
    }
  }, []);

  const selectTrendAgent = useCallback(async (agent) => {
    setSelectedTrendAgent(agent);
    setTrendActiveTab('aggregated');
    setAgentAggregatedData(null);
    setAgentWeekData(null);
    setAgentAggregatedLoading(true);
    setAgentWeekLoading(true);
    try {
      const [allRes, weekRes] = await Promise.all([
        api.get(`/reports/call-center/qa/agent-trends/${encodeURIComponent(agent.agent_name)}`),
        api.get(`/reports/call-center/qa/agent-trends/${encodeURIComponent(agent.agent_name)}/week`),
      ]);
      setAgentAggregatedData(allRes.data);
      setAgentWeekData(weekRes.data);
    } catch {
      setAgentAggregatedData(null);
      setAgentWeekData(null);
    } finally {
      setAgentAggregatedLoading(false);
      setAgentWeekLoading(false);
    }
  }, []);

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const activeItems = editableItems || evaluation?.items || [];
  const groupedItems = activeItems.length > 0
    ? activeItems.reduce((acc, item) => {
        if (!acc[item.section]) acc[item.section] = [];
        acc[item.section].push(item);
        return acc;
      }, {})
    : {};

  const editedBaseScore = activeItems.filter(i => i.section !== 'Bonus').reduce((s, i) => s + (i.awardedPoints || 0), 0);
  const editedBonus = activeItems.find(i => i.section === 'Bonus')?.awardedPoints || 0;
  const editedFinalScore = (evaluation?.autoFailTriggered && !autoFailDismissed) ? 0 : Math.min(editedBaseScore + editedBonus, 105);

  const displayRiskReason = evaluation
    ? evaluation.autoFailTriggered
      ? evaluation.riskReason
      : editedFinalScore >= 94 ? 'Strong performance across all evaluated criteria'
      : editedFinalScore >= 80 ? `Score of ${editedFinalScore}/105 — coaching opportunities identified; no critical compliance concerns`
      : editedFinalScore >= 70 ? `Score of ${editedFinalScore}/105 — performance gaps require supervisor review`
      : `Score of ${editedFinalScore}/105 — critical performance gaps across multiple rubric sections`
    : null;

  const canEvaluate = transcriptState === 'loaded' || (transcriptState === 'failed' && manualInput.trim().length > 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">QA Call Evaluation</h1>
          <p className="text-sm text-gray-500 mt-0.5">Rule-based quality scoring using the QA rubric and Knowledge Base</p>
        </div>
        <button
          onClick={() => navigate('/qa-dashboard')}
          className="flex items-center gap-1.5 text-sm font-medium text-amber-700 border border-amber-300 rounded-lg px-3 py-2 hover:bg-amber-50 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
      </div>

      {/* Filter Bar */}
      <div className="card">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Agent Name</label>
            <div className="relative">
              <User className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                className="input pl-9"
                placeholder="Search agent..."
                value={filters.agentName}
                autoComplete="off"
                onChange={e => {
                  const val = e.target.value;
                  setFilters(f => ({ ...f, agentName: val }));
                  clearTimeout(agentSuggestTimer.current);
                  if (val.trim().length >= 1) {
                    agentSuggestTimer.current = setTimeout(async () => {
                      try {
                        const res = await api.get('/reports/call-center/qa/agents', { params: { q: val.trim() } });
                        setAgentSuggestions(res.data || []);
                        setShowSuggestions(true);
                      } catch { setAgentSuggestions([]); }
                    }, 250);
                  } else {
                    setAgentSuggestions([]);
                    setShowSuggestions(false);
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { setShowSuggestions(false); fetchCalls(1); }
                  if (e.key === 'Escape') setShowSuggestions(false);
                }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onFocus={() => agentSuggestions.length > 0 && setShowSuggestions(true)}
              />
              {showSuggestions && agentSuggestions.length > 0 && (
                <ul className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {agentSuggestions.map(name => (
                    <li
                      key={name}
                      className="px-3 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-800 cursor-pointer"
                      onMouseDown={() => {
                        setFilters(f => ({ ...f, agentName: name }));
                        setShowSuggestions(false);
                        setAgentSuggestions([]);
                      }}
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input type="date" className="input" value={filters.dateFrom}
              onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input type="date" className="input" value={filters.dateTo}
              onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
          </div>
          <div className="flex gap-2 items-end">
            {/* Filter dropdown */}
            <div className="relative" ref={filterPanelRef}>
              <button
                className={`btn-secondary flex items-center gap-1.5 ${(filters.status !== 'all' || filters.autoFailOnly) ? 'ring-2 ring-amber-400' : ''}`}
                onClick={() => setShowFilterPanel(p => !p)}
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filter
                {(filters.status !== 'all' || filters.autoFailOnly) && (
                  <span className="bg-amber-400 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                    {(filters.status !== 'all' ? 1 : 0) + (filters.autoFailOnly ? 1 : 0)}
                  </span>
                )}
              </button>
              {showFilterPanel && (
                <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-72 space-y-4">
                  {/* Status */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Status</p>
                    <div className="flex gap-2">
                      {[['all', 'All'], ['evaluated', 'Evaluated'], ['not_evaluated', 'Not Evaluated']].map(([val, label]) => (
                        <button
                          key={val}
                          onClick={() => setFilters(f => ({ ...f, status: val }))}
                          className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${filters.status === val ? 'bg-amber-400 border-amber-400 text-white' : 'border-gray-200 text-gray-600 hover:border-amber-300'}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Auto-Fail */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Auto-Fail</p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.autoFailOnly}
                        onChange={e => setFilters(f => ({ ...f, autoFailOnly: e.target.checked }))}
                        className="w-4 h-4 accent-amber-400"
                      />
                      <span className="text-sm text-gray-700">Show auto-fail calls only</span>
                    </label>
                  </div>
                  {/* Quick Date Presets */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Quick Date</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        ['Today', 0],
                        ['Last 7 days', 7],
                        ['Last 30 days', 30],
                      ].map(([label, days]) => (
                        <button
                          key={label}
                          onClick={() => {
                            const to = new Date().toISOString().slice(0, 10);
                            const from = days === 0 ? to : new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
                            setFilters(f => ({ ...f, dateFrom: from, dateTo: to }));
                          }}
                          className="text-xs px-3 py-1.5 rounded-full border border-gray-200 text-gray-600 hover:border-amber-300 font-medium transition-colors"
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Apply */}
                  <button
                    className="btn-primary w-full"
                    onClick={() => { setShowFilterPanel(false); fetchCalls(1); }}
                  >
                    Apply Filters
                  </button>
                </div>
              )}
            </div>
            <button className="btn-primary" onClick={() => fetchCalls(1)}>
              <Search className="w-4 h-4 mr-1.5" />Search
            </button>
            <button className="btn-secondary" onClick={handleClearFilters} title="Clear filters">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1">

        {/* Call List */}
        <div className="card p-0 overflow-hidden self-start">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-400" />
              Calls
              {!listLoading && pagination.total > 0 && (
                <span className="text-gray-400 font-normal text-xs">({pagination.total.toLocaleString()})</span>
              )}
            </h2>
            <div className="flex items-center gap-2">
              <button
                className="flex items-center gap-1.5 text-xs font-medium text-amber-700 border border-amber-300 rounded-lg px-3 py-1.5 hover:bg-amber-50 transition-colors"
                onClick={openTrendModal}
              >
                <BarChart2 className="w-3.5 h-3.5" />
                Agent Trend Analysis
              </button>
            </div>
          </div>

          {listLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
            </div>
          ) : calls.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Phone className="w-8 h-8 mb-2" />
              <p className="text-sm">No calls found</p>
              <p className="text-xs mt-1">Try adjusting the filters</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Call Duration</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Call ID</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Eval Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {calls.map((call) => {
                      const isSelected = selectedCall?.id === call.id;
                      const qs = call.quality_score != null ? Number(call.quality_score) : null;
                      const scoreColor = qs == null ? 'text-gray-300'
                        : qs >= 94 ? 'text-emerald-600'
                        : qs >= 70 ? 'text-amber-600'
                        : 'text-red-600';
                      const evalScore = evaluationScores[call.id] != null ? Math.round(Number(evaluationScores[call.id])) : (call.qa_score != null ? Math.round(Number(call.qa_score)) : null);
                      const evalColor = evalScore == null ? 'text-gray-300'
                        : evalScore >= 94 ? 'text-emerald-600'
                        : evalScore >= 70 ? 'text-amber-600'
                        : 'text-red-600';
                      return (
                        <tr
                          key={call.id}
                          onClick={() => selectCall(call)}
                          className={`cursor-pointer transition-colors ${
                            isSelected ? 'bg-amber-50 border-l-2 border-l-amber-400' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900 truncate max-w-[130px]">{call.agent_name || '—'}</div>
                            <div className="text-xs text-gray-400">{channelLabel(call.channel_type)}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{formatDate(call.interaction_date)}</td>
                          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{formatDuration(call.handle_time)}</td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs text-gray-500">
                              {call.voicecall_name || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {evalScore != null ? (
                              <div className="flex items-center gap-1.5">
                                <span className={`font-semibold tabular-nums text-xs ${evalColor}`}>
                                  {evalScore}/105
                                </span>
                                {call.auto_fail && !evaluationScores[call.id] && (
                                  <span title="Auto-fail triggered" className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded px-1">AF</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500">Page {pagination.page} of {totalPages}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => fetchCalls(1)}
                      disabled={pagination.page <= 1}
                      className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="First page"
                    >
                      <ChevronsLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => fetchCalls(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Previous page"
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => fetchCalls(pagination.page + 1)}
                      disabled={pagination.page >= totalPages}
                      className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Next page"
                    >
                      <ChevronRight className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => fetchCalls(totalPages)}
                      disabled={pagination.page >= totalPages}
                      className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Last page"
                    >
                      <ChevronsRight className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail Panel removed — clicking a call opens the modal directly */}
        {false && selectedCall && (
          <div className="space-y-4">
            {/* Call Metadata */}
            <div className="card">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="font-semibold text-gray-900">{selectedCall.agent_name || 'Unknown Agent'}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {channelLabel(selectedCall.channel_type)} · {formatDate(selectedCall.interaction_date)}
                  </p>
                </div>
                <button
                  onClick={() => setShowMaximized(true)}
                  title="Maximize"
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Handle Time', value: formatDuration(selectedCall.handle_time), icon: Clock },
                  { label: 'Hold Time', value: formatDuration(selectedCall.hold_time), icon: Clock },
                  { label: 'Talk Time', value: formatDuration(selectedCall.talk_time), icon: Mic },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                    <p className="text-sm font-semibold text-gray-800 tabular-nums">{value}</p>
                  </div>
                ))}
              </div>
              {(selectedCall.primary_intent_a || selectedCall.primary_intent_b || selectedCall.primary_intent_c) && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {[selectedCall.primary_intent_a, selectedCall.primary_intent_b, selectedCall.primary_intent_c]
                    .filter(Boolean)
                    .map((intent, i) => (
                      <span key={i} className="text-xs bg-amber-50 text-amber-800 px-2.5 py-0.5 rounded-full">{intent}</span>
                    ))}
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
              {[
                { id: 'transcript', label: 'Transcript' },
                { id: 'scorecard', label: evaluation ? `Scorecard — ${evaluation.finalScore}/105` : 'Scorecard' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex-1 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Transcript Tab */}
            {activeTab === 'transcript' && (
              <div className="card space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800 text-sm">Call Transcript</h3>
                  {transcriptState === 'loaded' && (
                    <span className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" />Auto-fetched from Salesforce
                    </span>
                  )}
                  {transcriptState === 'failed' && (
                    <span className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" />Manual entry required
                    </span>
                  )}
                </div>

                {transcriptState === 'loading' && (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-5 h-5 text-amber-400 animate-spin mr-2" />
                    <span className="text-sm text-gray-500">Fetching transcript from Salesforce...</span>
                  </div>
                )}

                {transcriptState === 'loaded' && (
                  <div className="bg-gray-50 rounded-xl p-4 max-h-72 overflow-y-auto scrollbar-thin text-xs leading-relaxed text-gray-700 font-mono whitespace-pre-wrap">
                    {transcriptText}
                  </div>
                )}

                {transcriptState === 'failed' && (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        {selectedCall.conversation_identifier
                          ? 'Could not fetch transcript from Salesforce. Paste it below to proceed with evaluation.'
                          : 'No conversation record found for this call. Paste the transcript below to evaluate.'}
                      </span>
                    </div>
                    <textarea
                      className="w-full h-52 px-3 py-2.5 border border-gray-300 rounded-xl text-xs text-gray-700 font-mono resize-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none"
                      placeholder={`Paste transcript here...\n[Agent] Sarah: Thank you for calling Sunny Benefits, this is Sarah speaking.\n[Member] John: Hi, I need help with my benefits card...`}
                      value={manualInput}
                      onChange={e => setManualInput(e.target.value)}
                    />
                  </div>
                )}

                {(transcriptState === 'loaded' || transcriptState === 'failed') && (
                  <div className="pt-1">
                    {evalError && (
                      <div className="mb-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
                        <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {evalError}
                      </div>
                    )}
                    <button
                      onClick={handleEvaluate}
                      disabled={evalLoading || !canEvaluate}
                      className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {evalLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Evaluating...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" />Evaluate</>
                      )}
                    </button>
                    {transcriptState === 'failed' && !manualInput.trim() && (
                      <p className="text-xs text-gray-400 text-center mt-1.5">Paste a transcript above to enable evaluation</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Scorecard Tab */}
            {activeTab === 'scorecard' && (
              !evaluation ? (
                <div className="card flex flex-col items-center justify-center py-14 text-gray-400">
                  <Sparkles className="w-9 h-9 mb-2 text-amber-300" />
                  <p className="text-sm font-medium">No evaluation yet</p>
                  <p className="text-xs mt-1">Go to Transcript and click Evaluate</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Score Summary */}
                  <div className="card">
                    {evaluation.autoFailTriggered && (
                      autoFailDismissed ? (
                        <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold">Auto-Fail Dismissed by Reviewer</p>
                            <p className="text-xs mt-0.5">Score now reflects actual rubric points. Original reason: {evaluation.autoFailReason}</p>
                          </div>
                          <button
                            onClick={() => { setAutoFailDismissed(false); setEditedCriteria(prev => new Set([...prev, '__autofail__'])); }}
                            className="text-xs underline shrink-0"
                          >Undo</button>
                        </div>
                      ) : (
                        <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                          <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold">Auto-Fail Triggered</p>
                            {evaluation.autoFailReason && (
                              <p className="text-xs mt-0.5">{evaluation.autoFailReason}</p>
                            )}
                          </div>
                          <button
                            onClick={() => { setAutoFailDismissed(true); setEditedCriteria(prev => new Set([...prev, '__autofail__'])); }}
                            className="text-xs underline shrink-0 text-red-600 hover:text-red-800"
                          >Dismiss</button>
                        </div>
                      )
                    )}
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-2">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${RISK_PILL[evaluation.riskLevel] || 'bg-gray-100 text-gray-600'}`}>
                          {evaluation.riskLevel}
                        </span>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${ROUTING_PILL[evaluation.reviewRouting] || 'bg-gray-100 text-gray-600'}`}>
                          {evaluation.reviewRouting}
                        </span>
                      </div>
                      <div className="flex items-center gap-5">
                        <ScoreCircle score={evaluation.finalScore} />
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <div className="bg-gray-50 rounded-xl p-3 text-center">
                            <p className="text-2xl font-bold text-gray-900 tabular-nums">{evaluation.baseScore}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Base</p>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-3 text-center">
                            <p className="text-2xl font-bold text-amber-500 tabular-nums">+{evaluation.bonus}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Bonus</p>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-3 text-center">
                            <p className="text-2xl font-bold text-gray-900 tabular-nums">{evaluation.rawScore}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Raw</p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                          <span>Score progress</span>
                          <span className="tabular-nums font-medium text-gray-600">{evaluation.finalScore} / 105</span>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${scoreBarColor(Math.round((evaluation.finalScore / 105) * 100))}`}
                            style={{ width: `${Math.min(100, (evaluation.finalScore / 105) * 100)}%` }}
                          />
                        </div>
                      </div>
                      {evaluation.primaryIssueCategory && (
                        <div className="flex items-center flex-wrap gap-1.5">
                          <span className="text-xs text-gray-400">Issue:</span>
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{evaluation.primaryIssueCategory}</span>
                          {evaluation.secondaryIssueCategory && evaluation.secondaryIssueCategory !== evaluation.primaryIssueCategory && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{evaluation.secondaryIssueCategory}</span>
                          )}
                        </div>
                      )}
                      {displayRiskReason && (
                        <p className="text-xs text-gray-500 leading-relaxed">{displayRiskReason}</p>
                      )}
                    </div>
                    {evaluation.evaluationConfidence && evaluation.evaluationConfidence !== 'High' && (
                      <div className="mt-3 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>
                          Confidence: {evaluation.evaluationConfidence}
                          {evaluation.evaluationLimitations?.length > 0 && ` — ${evaluation.evaluationLimitations.join('; ')}`}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Section Breakdown */}
                  <div className="space-y-2">
                    {Object.entries(groupedItems).map(([section, items]) => (
                      <SectionCard key={section} section={section} items={items} />
                    ))}
                  </div>

                  {/* Behavior Flags */}
                  {evaluation.behaviorFlags?.length > 0 && (
                    <div className="card border border-red-200 bg-red-50">
                      <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1.5">
                        <Shield className="w-4 h-4" />Behavior Flags
                      </h3>
                      <ul className="space-y-1.5">
                        {evaluation.behaviorFlags.map((f, i) => (
                          <li key={i} className="text-xs text-red-700 flex items-start gap-1.5">
                            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Strengths & Opportunities */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {evaluation.strengths?.length > 0 && (
                      <div className="card border border-emerald-200 bg-emerald-50">
                        <h3 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
                          <Star className="w-4 h-4" />Strengths
                        </h3>
                        <ul className="space-y-1.5">
                          {evaluation.strengths.map((s, i) => (
                            <li key={i} className="text-xs text-emerald-700 flex items-start gap-1.5">
                              <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {evaluation.opportunities?.length > 0 && (
                      <div className="card border border-amber-200 bg-amber-50">
                        <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
                          <TrendingDown className="w-4 h-4" />Opportunities
                        </h3>
                        <ul className="space-y-2">
                          {evaluation.opportunities.map((o, i) => (
                            <li key={i} className="text-xs text-amber-800 space-y-0.5">
                              <div className="flex items-start gap-1.5">
                                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                                <span>{typeof o === 'string' ? o : o.text}</span>
                              </div>
                              {o.failureType && <div className="ml-3.5 text-amber-600 font-medium">{o.failureType}</div>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Training & Coaching */}
                  {(evaluation.recommendedTraining?.length > 0 || evaluation.dispositionRecommendations?.length > 0) && (
                    <div className="card">
                      {evaluation.recommendedTraining?.length > 0 && (
                        <div className="mb-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                            <BookOpen className="w-4 h-4 text-gray-500" />Recommended Training
                          </h3>
                          <div className="flex flex-wrap gap-1.5">
                            {evaluation.recommendedTraining.map((t, i) => (
                              <span key={i} className="text-xs bg-gray-100 text-gray-700 border border-gray-200 px-2.5 py-0.5 rounded-full">{t}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {evaluation.dispositionRecommendations?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                            <MessageSquare className="w-4 h-4 text-gray-500" />Disposition Recommendations
                          </h3>
                          <ul className="space-y-1.5">
                            {evaluation.dispositionRecommendations.map((d, i) => (
                              <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />{d}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {evaluation.evidenceNotes?.length > 0 && (
                    <div className="card border border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />Evidence Notes
                      </h3>
                      <ul className="space-y-1.5">
                        {evaluation.evidenceNotes.map((n, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />{n}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {evaluation.evaluatedAt && (
                    <p className="text-xs text-gray-400 text-center">
                      Evaluated {new Date(evaluation.evaluatedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Maximized Modal */}
      {showMaximized && selectedCall && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 md:p-8 overflow-y-auto"
          onClick={e => { if (e.target === e.currentTarget) { setShowMaximized(false); setSelectedCall(null); } }}
        >
          <div className="bg-white rounded-2xl w-full max-w-5xl shadow-2xl my-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="font-bold text-gray-900 text-lg">{selectedCall.agent_name || 'Unknown Agent'}</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {channelLabel(selectedCall.channel_type)} · {formatDate(selectedCall.interaction_date)}
                </p>
              </div>
              <button onClick={() => { setShowMaximized(false); setSelectedCall(null); }} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Time Stats */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Handle Time', value: formatDuration(selectedCall.handle_time) },
                  { label: 'Hold Time', value: formatDuration(selectedCall.hold_time) },
                  { label: 'Talk Time', value: formatDuration(selectedCall.talk_time) },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">{label}</p>
                    <p className="text-base font-semibold text-gray-800 tabular-nums">{value}</p>
                  </div>
                ))}
              </div>

              {/* Intent Tags */}
              {(selectedCall.primary_intent_a || selectedCall.primary_intent_b || selectedCall.primary_intent_c) && (
                <div className="flex flex-wrap gap-2">
                  {[selectedCall.primary_intent_a, selectedCall.primary_intent_b, selectedCall.primary_intent_c]
                    .filter(Boolean).map((intent, i) => (
                      <span key={i} className="text-xs bg-amber-50 text-amber-800 px-3 py-1 rounded-full">{intent}</span>
                    ))}
                </div>
              )}

              {/* Tabs */}
              <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
                {[
                  { id: 'transcript', label: 'Transcript' },
                  { id: 'scorecard', label: evaluation ? `Scorecard — ${editedFinalScore}/105` : 'Scorecard' },
                ].map(({ id, label }) => (
                  <button key={id} onClick={() => setActiveTab(id)}
                    className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >{label}</button>
                ))}
              </div>

              {/* Transcript Tab */}
              {activeTab === 'transcript' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-800">Call Transcript</h3>
                    {transcriptState === 'loaded' && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />Auto-fetched from Salesforce</span>}
                    {transcriptState === 'failed' && <span className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Manual entry required</span>}
                  </div>
                  {transcriptState === 'loading' && (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="w-5 h-5 text-amber-400 animate-spin mr-2" />
                      <span className="text-sm text-gray-500">Fetching transcript from Salesforce...</span>
                    </div>
                  )}
                  {transcriptState === 'loaded' && (
                    <div className="bg-gray-50 rounded-xl p-5 max-h-[500px] overflow-y-auto text-sm leading-relaxed text-gray-700 font-mono whitespace-pre-wrap">
                      {transcriptText}
                    </div>
                  )}
                  {transcriptState === 'failed' && (
                    <div className="space-y-3">
                      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{selectedCall.conversation_identifier ? 'Could not fetch transcript from Salesforce. Paste it below to proceed with evaluation.' : 'No conversation record found for this call. Paste the transcript below to evaluate.'}</span>
                      </div>
                      <textarea
                        className="w-full h-64 px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-700 font-mono resize-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none"
                        placeholder="Paste transcript here..."
                        value={manualInput}
                        onChange={e => setManualInput(e.target.value)}
                      />
                    </div>
                  )}
                  {(transcriptState === 'loaded' || transcriptState === 'failed') && (
                    <div>
                      {evalError && (
                        <div className="mb-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                          <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />{evalError}
                        </div>
                      )}
                      <button onClick={handleEvaluate} disabled={evalLoading || !canEvaluate} className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed">
                        {evalLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Evaluating...</> : <><Sparkles className="w-4 h-4 mr-2" />Evaluate</>}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Scorecard Tab */}
              {activeTab === 'scorecard' && (
                !evaluation ? (
                  <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <Sparkles className="w-10 h-10 mb-3 text-amber-300" />
                    <p className="text-sm font-medium">No evaluation yet</p>
                    <p className="text-xs mt-1">Go to Transcript and click Evaluate</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {evaluation.autoFailTriggered && (
                      autoFailDismissed ? (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold">Auto-Fail Dismissed by Reviewer</p>
                            <p className="text-xs mt-0.5">Score now reflects actual rubric points. Original reason: {evaluation.autoFailReason}</p>
                          </div>
                          <button onClick={() => { setAutoFailDismissed(false); setEditedCriteria(prev => new Set([...prev, '__autofail__'])); setSaveState('idle'); }} className="text-xs font-semibold px-3 py-1 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 shrink-0 border border-amber-300">Undo</button>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                          <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="font-semibold">Auto-Fail Triggered</p>
                            {evaluation.autoFailReason && <p className="text-xs mt-0.5">{evaluation.autoFailReason}</p>}
                          </div>
                          <button onClick={() => { setAutoFailDismissed(true); setEditedCriteria(prev => new Set([...prev, '__autofail__'])); }} className="text-xs font-semibold px-3 py-1 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 shrink-0 border border-red-300">Dismiss</button>
                        </div>
                      )
                    )}
                    <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${RISK_PILL[evaluation.riskLevel] || 'bg-gray-100 text-gray-600'}`}>{evaluation.riskLevel}</span>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${ROUTING_PILL[evaluation.reviewRouting] || 'bg-gray-100 text-gray-600'}`}>{evaluation.reviewRouting}</span>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${evaluation.autoFailTriggered ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          Auto-Fail: {evaluation.autoFailTriggered ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex items-center gap-6">
                        <ScoreCircle score={editedFinalScore} />
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                            <p className="text-2xl font-bold text-gray-900 tabular-nums">{editedBaseScore}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Base</p>
                          </div>
                          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                            <p className="text-2xl font-bold text-amber-500 tabular-nums">+{editedBonus}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Bonus</p>
                          </div>
                          <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                            <p className="text-2xl font-bold text-gray-900 tabular-nums">{editedFinalScore}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Total</p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                          <span>Score progress{editedCriteria.size > 0 && <span className="ml-2 text-amber-500">({editedCriteria.size} item{editedCriteria.size > 1 ? 's' : ''} adjusted)</span>}</span>
                          <span className="tabular-nums font-medium text-gray-600">{editedFinalScore} / 105</span>
                        </div>
                        <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${scoreBarColor(Math.round((editedFinalScore / 105) * 100))}`}
                            style={{ width: `${Math.min(100, (editedFinalScore / 105) * 100)}%` }}
                          />
                        </div>
                      </div>
                      {evaluation.primaryIssueCategory && (
                        <div className="flex items-center flex-wrap gap-1.5">
                          <span className="text-xs text-gray-400">Issue:</span>
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{evaluation.primaryIssueCategory}</span>
                          {evaluation.secondaryIssueCategory && evaluation.secondaryIssueCategory !== evaluation.primaryIssueCategory && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{evaluation.secondaryIssueCategory}</span>
                          )}
                        </div>
                      )}
                      {displayRiskReason && (
                        <p className="text-xs text-gray-500 leading-relaxed">{displayRiskReason}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        {Object.entries(groupedItems).map(([section, items]) => (
                          <SectionCard key={section} section={section} items={items} onScoreChange={handleItemScoreChange} editedCriteria={editedCriteria} />
                        ))}
                      </div>
                      <div className="space-y-3">
                        {evaluation.behaviorFlags?.length > 0 && (
                          <div className="card border border-red-200 bg-red-50">
                            <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1.5"><Shield className="w-4 h-4" />Behavior Flags</h3>
                            <ul className="space-y-1.5">{evaluation.behaviorFlags.map((f, i) => <li key={i} className="text-xs text-red-700 flex items-start gap-1.5"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />{f}</li>)}</ul>
                          </div>
                        )}
                        {evaluation.strengths?.length > 0 && (
                          <div className="card border border-emerald-200 bg-emerald-50">
                            <h3 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-1.5"><Star className="w-4 h-4" />Strengths</h3>
                            <ul className="space-y-1.5">{evaluation.strengths.map((s, i) => <li key={i} className="text-xs text-emerald-700 flex items-start gap-1.5"><CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{s}</li>)}</ul>
                          </div>
                        )}
                        {evaluation.opportunities?.length > 0 && (
                          <div className="card border border-amber-200 bg-amber-50">
                            <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1.5"><TrendingDown className="w-4 h-4" />Opportunities</h3>
                            <ul className="space-y-2">{evaluation.opportunities.map((o, i) => <li key={i} className="text-xs text-amber-800 space-y-0.5"><div className="flex items-start gap-1.5"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" /><span>{typeof o === 'string' ? o : o.text}</span></div>{o.failureType && <div className="ml-3.5 text-amber-600 font-medium">{o.failureType}</div>}</li>)}</ul>
                          </div>
                        )}
                        {(evaluation.recommendedTraining?.length > 0 || evaluation.dispositionRecommendations?.length > 0) && (
                          <div className="card">
                            {evaluation.recommendedTraining?.length > 0 && (
                              <div className="mb-3">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-gray-500" />Recommended Training</h3>
                                <div className="flex flex-wrap gap-1.5">{evaluation.recommendedTraining.map((t, i) => <span key={i} className="text-xs bg-gray-100 text-gray-700 border border-gray-200 px-2.5 py-0.5 rounded-full">{t}</span>)}</div>
                              </div>
                            )}
                            {evaluation.dispositionRecommendations?.length > 0 && (
                              <div>
                                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><MessageSquare className="w-4 h-4 text-gray-500" />Disposition Recommendations</h3>
                                <ul className="space-y-1.5">{evaluation.dispositionRecommendations.map((d, i) => <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />{d}</li>)}</ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {evaluation.evidenceNotes?.length > 0 && (
                      <div className="card border border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-500" />Evidence Notes</h3>
                        <ul className="space-y-1.5">{evaluation.evidenceNotes.map((n, i) => <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />{n}</li>)}</ul>
                      </div>
                    )}
                    {evaluation.evaluatedAt && (
                      <p className="text-xs text-gray-400 text-center">
                        Evaluated {new Date(evaluation.evaluatedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                      </p>
                    )}

                    {/* Save */}
                    <div className="border-t border-gray-100 pt-4">
                      <button
                        onClick={() => handleSaveEvaluation(false)}
                        disabled={saveState === 'saving' || saveState === 'saved' || (isEvaluationSaved && editedCriteria.size === 0)}
                        className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {saveState === 'saving' ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                        ) : saveState === 'saved' ? (
                          <><CheckCircle className="w-4 h-4 mr-2" />Saved</>
                        ) : editedCriteria.size > 0 ? (
                          <><Save className="w-4 h-4 mr-2" />Save Adjusted Score — {editedFinalScore}/105</>
                        ) : (
                          <><Database className="w-4 h-4 mr-2" />Save Score — {editedFinalScore}/105</>
                        )}
                      </button>
                      {saveState === 'error' && (
                        <p className="text-xs text-red-600 flex items-center gap-1.5 mt-2">
                          <XCircle className="w-3.5 h-3.5" />Save failed — please try again.
                        </p>
                      )}
                    </div>

                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trend Analysis Modal */}
      {showTrendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-amber-500" />
                <h2 className="font-semibold text-gray-800">Agent Trend Analysis</h2>
              </div>
              <button onClick={() => setShowTrendModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex flex-1 overflow-hidden">

              {/* Left — Agent List */}
              <div className="w-64 border-r border-gray-100 flex flex-col shrink-0">
                <div className="p-3 border-b border-gray-100">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                    <input
                      className="input pl-8 text-sm py-1.5"
                      placeholder="Filter agents..."
                      value={trendAgentSearch}
                      onChange={e => setTrendAgentSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {trendAgentsLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
                    </div>
                  ) : trendAgents.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No scored calls yet</p>
                  ) : (
                    trendAgents
                      .filter(a => !trendAgentSearch.trim() || a.agent_name.toLowerCase().includes(trendAgentSearch.toLowerCase()))
                      .map(agent => (
                        <button
                          key={agent.agent_name}
                          onClick={() => selectTrendAgent(agent)}
                          className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-amber-50 transition-colors ${selectedTrendAgent?.agent_name === agent.agent_name ? 'bg-amber-50 border-l-2 border-l-amber-400' : ''}`}
                        >
                          <p className="text-sm font-medium text-gray-800 truncate">{agent.agent_name}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {agent.total_calls} call{Number(agent.total_calls) !== 1 ? 's' : ''} · Avg {Number(agent.avg_score).toFixed(0)}/105
                          </p>
                        </button>
                      ))
                  )}
                </div>
              </div>

              {/* Right — Detail Pane */}
              <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {!selectedTrendAgent ? (
                  <div className="flex flex-col items-center justify-center flex-1 text-gray-400">
                    <BarChart2 className="w-10 h-10 mb-2 text-gray-200" />
                    <p className="text-sm">Select an agent to view their trend</p>
                  </div>
                ) : (
                  <>
                    {/* Agent Header */}
                    <div className="px-5 py-3 border-b border-gray-100">
                      <p className="font-semibold text-gray-800">{selectedTrendAgent.agent_name}</p>
                      <p className="text-xs text-gray-400">
                        {selectedTrendAgent.total_calls} calls scored · latest {selectedTrendAgent.latest_call_date ? String(selectedTrendAgent.latest_call_date).slice(0, 10) : '—'}
                      </p>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-gray-100 px-5">
                      {[['aggregated', 'All Time'], ['week', 'This Week'], ['session', 'Current Session']].map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setTrendActiveTab(key)}
                          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${trendActiveTab === key ? 'border-amber-400 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {/* Tab Content */}
                    <div className="flex-1 overflow-y-auto p-5">

                      {/* ALL TIME TAB */}
                      {trendActiveTab === 'aggregated' && (
                        agentAggregatedLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                          </div>
                        ) : !agentAggregatedData ? (
                          <p className="text-sm text-gray-400 text-center py-10">No data available</p>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid grid-cols-4 gap-3">
                              {[
                                { label: 'Total Calls', value: agentAggregatedData.totalCalls, color: 'text-gray-800' },
                                { label: 'Avg Score /105', value: agentAggregatedData.avgScore, color: agentAggregatedData.avgScore >= 94 ? 'text-emerald-600' : agentAggregatedData.avgScore >= 70 ? 'text-amber-600' : 'text-red-600' },
                                { label: 'Auto-Fails', value: agentAggregatedData.autoFailCount, color: agentAggregatedData.autoFailCount > 0 ? 'text-red-600' : 'text-emerald-600' },
                                { label: 'Overrides', value: agentAggregatedData.overrideCount, color: 'text-gray-600' },
                              ].map(({ label, value, color }) => (
                                <div key={label} className="bg-gray-50 rounded-xl p-3 text-center">
                                  <p className={`text-xl font-bold tabular-nums ${color}`}>{value}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">{label}</p>
                                </div>
                              ))}
                            </div>

                            {agentAggregatedData.trend && (
                              <p className={`text-xs font-medium flex items-center gap-1 ${agentAggregatedData.trend === 'improving' ? 'text-emerald-600' : agentAggregatedData.trend === 'declining' ? 'text-red-500' : 'text-amber-500'}`}>
                                {agentAggregatedData.trend === 'improving' ? <TrendingUp className="w-3.5 h-3.5" /> : agentAggregatedData.trend === 'declining' ? <TrendingDown className="w-3.5 h-3.5" /> : null}
                                Score is {agentAggregatedData.trend} over recent calls
                              </p>
                            )}

                            {agentAggregatedData.scoreTrend?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recent Scores</p>
                                <div className="flex items-end gap-1.5 h-16">
                                  {agentAggregatedData.scoreTrend.map((pt, i) => (
                                    <div key={i} className="flex flex-col items-center flex-1 gap-1" title={`${String(pt.date).slice(0, 10)}: ${pt.score}/105`}>
                                      <div
                                        className={`w-full rounded-sm ${pt.autoFail && !pt.overridden ? 'bg-red-400' : pt.overridden ? 'bg-amber-400' : 'bg-emerald-400'}`}
                                        style={{ height: `${Math.max(4, Math.round((pt.score / 105) * 56))}px` }}
                                      />
                                      <span className="text-[9px] text-gray-400 tabular-nums">{pt.score}</span>
                                    </div>
                                  ))}
                                </div>
                                <p className="text-[10px] text-gray-300 mt-1">Oldest → Most Recent · Red = auto-fail · Amber = override</p>
                              </div>
                            )}

                            {agentAggregatedData.commonStrengths?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-emerald-700 mb-1.5">Common Strengths</p>
                                <ul className="space-y-1">
                                  {agentAggregatedData.commonStrengths.map((s, i) => (
                                    <li key={i} className="text-xs text-emerald-800 flex items-start gap-1.5">
                                      <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500" />{s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {agentAggregatedData.commonIssues?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-amber-700 mb-1.5">Recurring Issues</p>
                                <ul className="space-y-1">
                                  {agentAggregatedData.commonIssues.map((s, i) => (
                                    <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5">
                                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />{s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {agentAggregatedData.commonFlags?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-red-700 mb-1.5">Common Behavior Flags</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {agentAggregatedData.commonFlags.map((f, i) => (
                                    <span key={i} className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-full px-2.5 py-0.5">{f}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      )}

                      {/* THIS WEEK TAB */}
                      {trendActiveTab === 'week' && (
                        agentWeekLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                          </div>
                        ) : !agentWeekData || agentWeekData.totalCalls === 0 ? (
                          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <Info className="w-8 h-8 mb-2 text-gray-200" />
                            <p className="text-sm">No calls scored this week</p>
                            <p className="text-xs mt-1">Calls scored from Monday onwards will appear here</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid grid-cols-4 gap-3">
                              {[
                                { label: 'Calls This Week', value: agentWeekData.totalCalls, color: 'text-gray-800' },
                                { label: 'Avg Score /105', value: agentWeekData.avgScore, color: agentWeekData.avgScore >= 94 ? 'text-emerald-600' : agentWeekData.avgScore >= 70 ? 'text-amber-600' : 'text-red-600' },
                                { label: 'Auto-Fails', value: agentWeekData.autoFailCount, color: agentWeekData.autoFailCount > 0 ? 'text-red-600' : 'text-emerald-600' },
                                { label: 'Overrides', value: agentWeekData.overrideCount, color: 'text-gray-600' },
                              ].map(({ label, value, color }) => (
                                <div key={label} className="bg-gray-50 rounded-xl p-3 text-center">
                                  <p className={`text-xl font-bold tabular-nums ${color}`}>{value}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">{label}</p>
                                </div>
                              ))}
                            </div>

                            {agentWeekData.trend && agentWeekData.totalCalls >= 2 && (
                              <p className={`text-xs font-medium flex items-center gap-1 ${agentWeekData.trend === 'improving' ? 'text-emerald-600' : agentWeekData.trend === 'declining' ? 'text-red-500' : 'text-amber-500'}`}>
                                {agentWeekData.trend === 'improving' ? <TrendingUp className="w-3.5 h-3.5" /> : agentWeekData.trend === 'declining' ? <TrendingDown className="w-3.5 h-3.5" /> : null}
                                Score is {agentWeekData.trend} over this week
                              </p>
                            )}

                            {agentWeekData.scoreTrend?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Scores This Week</p>
                                <div className="flex items-end gap-1.5 h-16">
                                  {agentWeekData.scoreTrend.map((pt, i) => (
                                    <div key={i} className="flex flex-col items-center flex-1 gap-1" title={`${String(pt.date).slice(0, 10)}: ${pt.score}/105`}>
                                      <div
                                        className={`w-full rounded-sm ${pt.autoFail && !pt.overridden ? 'bg-red-400' : pt.overridden ? 'bg-amber-400' : 'bg-emerald-400'}`}
                                        style={{ height: `${Math.max(4, Math.round((pt.score / 105) * 56))}px` }}
                                      />
                                      <span className="text-[9px] text-gray-400 tabular-nums">{pt.score}</span>
                                    </div>
                                  ))}
                                </div>
                                <p className="text-[10px] text-gray-300 mt-1">Mon → Today · Red = auto-fail · Amber = override</p>
                              </div>
                            )}

                            {agentWeekData.commonStrengths?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-emerald-700 mb-1.5">Strengths This Week</p>
                                <ul className="space-y-1">
                                  {agentWeekData.commonStrengths.map((s, i) => (
                                    <li key={i} className="text-xs text-emerald-800 flex items-start gap-1.5">
                                      <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500" />{s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {agentWeekData.commonIssues?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-amber-700 mb-1.5">Issues This Week</p>
                                <ul className="space-y-1">
                                  {agentWeekData.commonIssues.map((s, i) => (
                                    <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5">
                                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />{s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {agentWeekData.commonFlags?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-red-700 mb-1.5">Behavior Flags</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {agentWeekData.commonFlags.map((f, i) => (
                                    <span key={i} className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-full px-2.5 py-0.5">{f}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      )}

                      {/* CURRENT SESSION TAB */}
                      {trendActiveTab === 'session' && (() => {
                        const trend = computeTrend(selectedTrendAgent.agent_name, evaluationHistory);
                        if (!trend) return (
                          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <Info className="w-8 h-8 mb-2 text-gray-200" />
                            <p className="text-sm">No session data yet</p>
                            <p className="text-xs mt-1">Open 2+ calls for this agent to see session trends</p>
                          </div>
                        );
                        return (
                          <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                              <div className="bg-gray-50 rounded-xl p-3 text-center">
                                <p className="text-xl font-bold text-gray-800 tabular-nums">{trend.count}</p>
                                <p className="text-xs text-gray-400 mt-0.5">Calls This Session</p>
                              </div>
                              <div className="bg-gray-50 rounded-xl p-3 text-center">
                                <p className={`text-xl font-bold tabular-nums ${trend.avgScore >= 94 ? 'text-emerald-600' : trend.avgScore >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{trend.avgScore}</p>
                                <p className="text-xs text-gray-400 mt-0.5">Avg Score /105</p>
                              </div>
                              <div className="bg-gray-50 rounded-xl p-3 text-center">
                                <p className={`text-xl font-bold tabular-nums ${trend.autoFailCount > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{trend.autoFailCount}</p>
                                <p className="text-xs text-gray-400 mt-0.5">Auto-Fails</p>
                              </div>
                            </div>
                            {trend.trend && (
                              <p className={`text-xs font-medium flex items-center gap-1 ${trend.trend === 'improving' ? 'text-emerald-600' : trend.trend === 'declining' ? 'text-red-500' : 'text-amber-500'}`}>
                                {trend.trend === 'improving' ? <TrendingUp className="w-3.5 h-3.5" /> : trend.trend === 'declining' ? <TrendingDown className="w-3.5 h-3.5" /> : null}
                                Score is {trend.trend} compared to previous call
                              </p>
                            )}
                            {trend.commonStrengths.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-emerald-700 mb-1.5">Common Strengths</p>
                                <ul className="space-y-1">
                                  {trend.commonStrengths.map((s, i) => (
                                    <li key={i} className="text-xs text-emerald-800 flex items-start gap-1.5">
                                      <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-500" />{s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {trend.commonIssues.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-amber-700 mb-1.5">Recurring Issues</p>
                                <ul className="space-y-1">
                                  {trend.commonIssues.map((s, i) => (
                                    <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5">
                                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />{s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

